import { useState, useRef, useEffect, useReducer } from "react";
import { CognitoService } from "./auth/CognitoService";
import ProtectedRoute from "./components/ProtectedRoute";
import PhoneInput, { isValidPhoneNumber } from "react-phone-number-input";
import { parsePhoneNumber } from "libphonenumber-js";
import flags from "react-phone-number-input/flags";
import "react-phone-number-input/style.css";

const TABS = ["Dashboard", "New Check", "Scheduled", "History"];

const brand = {
  teal:        "#00242E",   // Deep Teal — primary background
  tealLight:   "#00343F",   // card background
  tealMid:     "#004A5C",   // hover / elevated
  tealBorder:  "#005870",   // borders
  red:         "#EB1700",   // primary accent
  pink:        "#FF609E",   // gradient end
  redDark:     "#C21200",   // darker red
  turquoise:   "#00FFB8",   // secondary accent / success
  lime:        "#B9FF00",   // tertiary accent
  white:       "#FFFFFF",
  offWhite:    "#E4D6E1",   // Light Grey
  muted:       "#7AACB5",   // muted text
  mutedDark:   "#3A6A75",   // darker muted
  success:     "#00FFB8",   // turquoise
  danger:      "#EB1700",   // red
};

const S: Record<string, React.CSSProperties> = {
  page:       { backgroundColor: brand.teal, minHeight: "100vh", color: brand.white, fontFamily: "'Segoe UI', Arial, sans-serif" },
  header:     { backgroundColor: "#000B0E", borderBottom: `1px solid ${brand.tealBorder}`, position: "sticky", top: 0, zIndex: 50, backdropFilter: "blur(8px)" },
  card:       { backgroundColor: brand.tealLight, border: `1px solid ${brand.tealBorder}`, borderLeft: `3px solid ${brand.turquoise}`, borderRadius: 12 },
  inputBase:  { backgroundColor: brand.teal, border: `1px solid ${brand.tealBorder}`, borderRadius: 8, padding: "10px 14px", color: brand.white, width: "100%", fontSize: 14, outline: "none", boxSizing: "border-box" },
  primaryBtn: { background: `linear-gradient(135deg, ${brand.red}, ${brand.pink})`, color: brand.white, border: "none", borderRadius: 8, padding: "12px 24px", fontWeight: 700, fontSize: 14, cursor: "pointer", letterSpacing: "0.05em", textTransform: "uppercase", width: "100%" },
  ghostBtn:   { backgroundColor: "transparent", border: `1px solid ${brand.tealBorder}`, borderRadius: 8, padding: "8px 16px", color: brand.muted, cursor: "pointer", fontSize: 13, fontWeight: 500 },
  label:      { fontSize: 12, color: brand.muted, marginBottom: 6, display: "block", letterSpacing: "0.08em", textTransform: "uppercase", fontWeight: 600 },
  sectionHd:  { fontSize: 11, color: brand.turquoise, letterSpacing: "0.12em", textTransform: "uppercase", fontWeight: 700, marginBottom: 16 },
};

// ---------------------------------------------------------------------------
// Timezone data and helpers
// ---------------------------------------------------------------------------
const COUNTRY_TIMEZONES: Record<string, string[]> = {
  GB: ['Europe/London'], IE: ['Europe/Dublin'], NZ: ['Pacific/Auckland'],
  JP: ['Asia/Tokyo'], IN: ['Asia/Kolkata'], SG: ['Asia/Singapore'], HK: ['Asia/Hong_Kong'],
  MY: ['Asia/Kuala_Lumpur'], PH: ['Asia/Manila'], TH: ['Asia/Bangkok'], VN: ['Asia/Ho_Chi_Minh'],
  KR: ['Asia/Seoul'], TW: ['Asia/Taipei'], AE: ['Asia/Dubai'], SA: ['Asia/Riyadh'],
  KW: ['Asia/Kuwait'], QA: ['Asia/Qatar'], PK: ['Asia/Karachi'], BD: ['Asia/Dhaka'],
  ZA: ['Africa/Johannesburg'], NG: ['Africa/Lagos'], KE: ['Africa/Nairobi'], GH: ['Africa/Accra'],
  EG: ['Africa/Cairo'], IL: ['Asia/Jerusalem'], TR: ['Europe/Istanbul'],
  DE: ['Europe/Berlin'], FR: ['Europe/Paris'], IT: ['Europe/Rome'], ES: ['Europe/Madrid'],
  NL: ['Europe/Amsterdam'], BE: ['Europe/Brussels'], PT: ['Europe/Lisbon'],
  SE: ['Europe/Stockholm'], NO: ['Europe/Oslo'], DK: ['Europe/Copenhagen'], FI: ['Europe/Helsinki'],
  AT: ['Europe/Vienna'], CH: ['Europe/Zurich'], PL: ['Europe/Warsaw'], CZ: ['Europe/Prague'],
  RO: ['Europe/Bucharest'], GR: ['Europe/Athens'], HU: ['Europe/Budapest'], IS: ['Atlantic/Reykjavik'],
  US: ['America/New_York', 'America/Chicago', 'America/Denver', 'America/Phoenix', 'America/Los_Angeles', 'America/Anchorage', 'Pacific/Honolulu'],
  CA: ['America/St_Johns', 'America/Halifax', 'America/Toronto', 'America/Winnipeg', 'America/Edmonton', 'America/Vancouver'],
  AU: ['Australia/Sydney', 'Australia/Melbourne', 'Australia/Brisbane', 'Australia/Adelaide', 'Australia/Perth', 'Australia/Darwin', 'Australia/Hobart'],
  MX: ['America/Mexico_City', 'America/Cancun', 'America/Chihuahua', 'America/Tijuana'],
  BR: ['America/Sao_Paulo', 'America/Manaus', 'America/Fortaleza', 'America/Recife', 'America/Belem'],
  RU: ['Europe/Moscow', 'Europe/Samara', 'Asia/Yekaterinburg', 'Asia/Omsk', 'Asia/Krasnoyarsk', 'Asia/Irkutsk', 'Asia/Yakutsk', 'Asia/Vladivostok', 'Asia/Kamchatka'],
  CN: ['Asia/Shanghai', 'Asia/Urumqi'], ID: ['Asia/Jakarta', 'Asia/Makassar', 'Asia/Jayapura'],
};

const ALL_TIMEZONES = [
  'Pacific/Honolulu', 'America/Anchorage', 'America/Los_Angeles', 'America/Phoenix',
  'America/Denver', 'America/Chicago', 'America/New_York', 'America/Toronto',
  'America/Halifax', 'America/St_Johns', 'America/Sao_Paulo', 'Atlantic/Azores',
  'Europe/London', 'Europe/Dublin', 'Europe/Paris', 'Europe/Berlin', 'Europe/Athens',
  'Europe/Moscow', 'Asia/Dubai', 'Asia/Karachi', 'Asia/Kolkata', 'Asia/Dhaka',
  'Asia/Bangkok', 'Asia/Singapore', 'Asia/Tokyo', 'Australia/Perth', 'Australia/Darwin',
  'Australia/Adelaide', 'Australia/Brisbane', 'Australia/Sydney', 'Pacific/Auckland',
];

function getTimezonesForPhone(phoneNumber: string): string[] {
  if (!phoneNumber) return ALL_TIMEZONES;
  try {
    const parsed = parsePhoneNumber(phoneNumber);
    if (!parsed?.country) return ALL_TIMEZONES;
    return COUNTRY_TIMEZONES[parsed.country] || ALL_TIMEZONES;
  } catch { return ALL_TIMEZONES; }
}

function localToUtc(dateStr: string, timeStr: string, ianaTimezone: string): string {
  const [year, month, day] = dateStr.split('-').map(Number);
  const [hour, minute] = timeStr.split(':').map(Number);
  const fmtParts = (d: Date) => {
    const p: Record<string, number> = {};
    new Intl.DateTimeFormat('en-CA', {
      timeZone: ianaTimezone, year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit', hour12: false,
    }).formatToParts(d).forEach(({ type, value }) => { if (type !== 'literal') p[type] = parseInt(value, 10); });
    return p;
  };
  let guess = new Date(Date.UTC(year, month - 1, day, hour, minute));
  for (let i = 0; i < 2; i++) {
    const local = fmtParts(guess);
    const localMs = Date.UTC(local.year, local.month - 1, local.day, local.hour, local.minute);
    const targetMs = Date.UTC(year, month - 1, day, hour, minute);
    guess = new Date(guess.getTime() + (targetMs - localMs));
  }
  return guess.toISOString();
}

function localDateStartToUtcIso(dateStr: string): string {
  const [y, m, d] = dateStr.split("-").map(Number);
  return new Date(y, m - 1, d, 0, 0, 0, 0).toISOString();
}
function localDateExclusiveEndToUtcIso(dateStr: string): string {
  const [y, m, d] = dateStr.split("-").map(Number);
  return new Date(y, m - 1, d + 1, 0, 0, 0, 0).toISOString();
}
function todayLocalDateStr(): string {
  const d = new Date();
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}
function addDaysToLocalDateStr(dateStr: string, days: number): string {
  const [y, m, d] = dateStr.split("-").map(Number);
  const dt = new Date(y, m - 1, d + days);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${dt.getFullYear()}-${pad(dt.getMonth() + 1)}-${pad(dt.getDate())}`;
}

function getTimezoneAbbr(tz: string, at?: Date): string {
  try {
    return new Intl.DateTimeFormat('en-GB', { timeZone: tz, timeZoneName: 'short' })
      .formatToParts(at ?? new Date()).find(p => p.type === 'timeZoneName')?.value || '';
  } catch { return ''; }
}

function formatTimezone(tz: string): string {
  const abbr = getTimezoneAbbr(tz);
  const parts = tz.split('/');
  const city = parts[parts.length - 1].replace(/_/g, ' ');
  const region = parts.length > 1 ? parts[parts.length - 2].replace(/_/g, ' ') : '';
  const top = ['America', 'Europe', 'Asia', 'Australia', 'Pacific', 'Atlantic', 'Africa', 'Indian'];
  const label = region && !top.includes(region) ? `${region} - ${city}` : city;
  return abbr ? `${label} (${abbr})` : tz;
}

function formatLocalDateTime(iso: string): string {
  try {
    return new Intl.DateTimeFormat('en-GB', {
      year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit',
    }).format(new Date(iso)).replace(',', '');
  } catch { return ''; }
}

function formatInTimezone(iso: string, tz: string): string {
  try {
    const d = new Date(iso);
    const parts: Record<string, string> = {};
    new Intl.DateTimeFormat('en-GB', {
      timeZone: tz, year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', hour12: false,
    }).formatToParts(d).forEach(({ type, value }) => { parts[type] = value; });
    return `${parts.day}/${parts.month}/${parts.year} ${parts.hour}:${parts.minute}`;
  } catch { return ''; }
}

function parseLocalDateTimeParts(iso: string): { date: string; time: string } | null {
  const m = iso.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/);
  if (!m) return null;
  return { date: `${m[1]}-${m[2]}-${m[3]}`, time: `${m[4]}:${m[5]}` };
}

function TimezoneSelect({ value, onChange, timezones }: { value: string; onChange: (v: string) => void; timezones: string[] }) {
  return (
    <select value={value} onChange={e => onChange(e.target.value)} style={{ ...S.inputBase, cursor: 'pointer' }}>
      <option value="">Select timezone…</option>
      {timezones.map(tz => <option key={tz} value={tz}>{formatTimezone(tz)}</option>)}
    </select>
  );
}

function Badge({ status }: { status: string }) {
  const map = {
    correct:   { bg: "rgba(0,255,184,0.15)",  color: brand.turquoise, border: "rgba(0,255,184,0.3)" },
    incorrect: { bg: "rgba(235,23,0,0.15)",   color: brand.red,       border: "rgba(235,23,0,0.3)" },
    scheduled: { bg: "rgba(0,255,184,0.12)",  color: brand.turquoise, border: "rgba(0,255,184,0.25)" },
    dialling:  { bg: "rgba(0,176,188,0.15)",  color: "#00B0BC",       border: "rgba(0,176,188,0.3)" },
    completed: { bg: "rgba(0,255,184,0.15)",  color: brand.turquoise, border: "rgba(0,255,184,0.3)" },
    failed:    { bg: "rgba(235,23,0,0.15)",   color: brand.red,       border: "rgba(235,23,0,0.3)" },
    pending:   { bg: "rgba(0,255,184,0.10)",  color: brand.turquoise, border: "rgba(0,255,184,0.2)" },
  };
  const c = map[status as keyof typeof map] || map.pending;
  return (
    <span style={{ background: c.bg, color: c.color, border: `1px solid ${c.border}`, borderRadius: 20, padding: "2px 10px", fontSize: 11, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase" }}>
      {status}
    </span>
  );
}

const RedLine = () => (
  <div style={{ height: 2, background: `linear-gradient(90deg, ${brand.red}, ${brand.pink}, transparent)`, margin: "0 0 20px 0", borderRadius: 1 }} />
);

function RefreshButton({ onRefresh }: { onRefresh: () => void }) {
  return (
    <button onClick={onRefresh} title="Refresh"
      style={{ ...S.ghostBtn, padding: "5px 10px", fontSize: 15, lineHeight: 1, color: brand.muted }}>
      ↻
    </button>
  );
}

// ---------------------------------------------------------------------------
// Responsive matchMedia hook
// ---------------------------------------------------------------------------
function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState(() => window.matchMedia(query).matches);
  useEffect(() => {
    const mql = window.matchMedia(query);
    const handler = (e: MediaQueryListEvent) => setMatches(e.matches);
    setMatches(mql.matches);
    mql.addEventListener("change", handler);
    return () => mql.removeEventListener("change", handler);
  }, [query]);
  return matches;
}

// ---------------------------------------------------------------------------
// Web Audio player hook
// ---------------------------------------------------------------------------
function useAudioPlayer(audioBuffer) {
  const ctxRef = useRef(null); const sourceRef = useRef(null);
  const startRef = useRef(0); const offsetRef = useRef(0); const rafRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(1);
  const gainRef = useRef(null);

  useEffect(() => { stop(); setProgress(0); setCurrentTime(0); offsetRef.current = 0; setDuration(audioBuffer ? audioBuffer.duration : 0); }, [audioBuffer]);
  useEffect(() => () => { stop(); ctxRef.current?.close(); }, []);

  function getCtx() {
    if (!ctxRef.current || ctxRef.current.state === "closed") {
      ctxRef.current = new ((window as any).AudioContext || (window as any).webkitAudioContext)();
      gainRef.current = ctxRef.current.createGain();
      gainRef.current.connect(ctxRef.current.destination);
    }
    return ctxRef.current;
  }
  function stop() { cancelAnimationFrame(rafRef.current); try { sourceRef.current?.stop(); } catch (_) {} sourceRef.current = null; setPlaying(false); }
  function tick() {
    if (!ctxRef.current || !audioBuffer) return;
    const e = ctxRef.current.currentTime - startRef.current + offsetRef.current;
    const c = Math.min(e, audioBuffer.duration);
    setCurrentTime(c); setProgress((c / audioBuffer.duration) * 100);
    if (c < audioBuffer.duration) rafRef.current = requestAnimationFrame(tick);
    else { offsetRef.current = 0; setProgress(0); setCurrentTime(0); setPlaying(false); }
  }
  function play() {
    if (!audioBuffer) return;
    const ctx = getCtx(); if (ctx.state === "suspended") ctx.resume();
    const src = ctx.createBufferSource(); src.buffer = audioBuffer;
    gainRef.current.gain.value = volume; src.connect(gainRef.current);
    src.start(0, offsetRef.current); sourceRef.current = src;
    startRef.current = ctx.currentTime; setPlaying(true); rafRef.current = requestAnimationFrame(tick);
  }
  function pause() { if (ctxRef.current) offsetRef.current = ctxRef.current.currentTime - startRef.current + offsetRef.current; stop(); }
  function seek(pct) { const was = playing; if (was) pause(); offsetRef.current = (pct / 100) * audioBuffer.duration; setCurrentTime(offsetRef.current); setProgress(pct); if (was) play(); }
  function setVol(v) { setVolume(v); if (gainRef.current) gainRef.current.gain.value = v; }
  return { playing, progress, currentTime, duration, volume, play, pause, seek, setVol };
}

// ---------------------------------------------------------------------------
// Audio Player UI
// ---------------------------------------------------------------------------
function AudioPlayer({ audioBuffer, label, accent = brand.red, noAudioLabel = "No audio available" }) {
  const { playing, progress, currentTime, duration, volume, play, pause, seek, setVol } = useAudioPlayer(audioBuffer);
  const fmt = (s) => (!s || isNaN(s)) ? "0:00" : `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, "0")}`;
  const handleSeek = (e) => { const r = e.currentTarget.getBoundingClientRect(); seek(Math.max(0, Math.min(100, ((e.clientX - r.left) / r.width) * 100))); };

  return (
    <div style={{ background: "rgba(0,0,0,0.2)", border: `1px solid ${brand.tealBorder}`, borderRadius: 10, padding: 16 }}>
      <div style={{ fontSize: 11, color: accent, letterSpacing: "0.08em", textTransform: "uppercase", fontWeight: 700, marginBottom: 12 }}>{label}</div>
      {!audioBuffer ? (
        <p style={{ color: brand.mutedDark, fontSize: 13, margin: 0 }}>{noAudioLabel}</p>
      ) : (
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <button onClick={playing ? pause : play}
            style={{ width: 36, height: 36, borderRadius: "50%", background: `linear-gradient(135deg, ${brand.red}, ${brand.pink})`, border: "none", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
            {playing
              ? <svg width="14" height="14" fill={brand.white} viewBox="0 0 24 24"><rect x="6" y="4" width="4" height="16" rx="1"/><rect x="14" y="4" width="4" height="16" rx="1"/></svg>
              : <svg width="14" height="14" fill={brand.white} viewBox="0 0 24 24" style={{ marginLeft: 2 }}><polygon points="5,3 19,12 5,21"/></svg>}
          </button>
          <span style={{ fontSize: 12, color: brand.muted, width: 38, flexShrink: 0, fontVariantNumeric: "tabular-nums" }}>{fmt(currentTime)}</span>
          <div onClick={handleSeek} style={{ flex: 1, height: 4, background: brand.tealBorder, borderRadius: 2, cursor: "pointer", position: "relative" }}>
            <div style={{ height: 4, borderRadius: 2, background: `linear-gradient(90deg, ${brand.red}, ${brand.pink})`, width: `${progress}%`, pointerEvents: "none" }} />
          </div>
          <span style={{ fontSize: 12, color: brand.muted, width: 38, flexShrink: 0, textAlign: "right", fontVariantNumeric: "tabular-nums" }}>{fmt(duration)}</span>
          <input type="range" min="0" max="1" step="0.05" value={volume} onChange={e => setVol(parseFloat(e.target.value))}
            style={{ width: 60, flexShrink: 0, accentColor: brand.red, cursor: "pointer" }} />
        </div>
      )}
    </div>
  );
}

function TranscriptBox({ label, text, accent }) {
  if (!text) return null;
  return (
    <div style={{ marginTop: 10 }}>
      <div style={{ fontSize: 10, color: accent, letterSpacing: "0.08em", textTransform: "uppercase", fontWeight: 700, marginBottom: 5 }}>{label}</div>
      <p style={{ color: brand.offWhite, fontSize: 12, fontStyle: "italic", background: "rgba(0,0,0,0.2)", borderRadius: 6, padding: "10px 12px", margin: 0, lineHeight: 1.6 }}>"{text}"</p>
    </div>
  );
}

function MultilingualBadge() {
  return (
    <span style={{ display:"inline-block", background:"rgba(0,255,184,0.12)", border:"1px solid rgba(0,255,184,0.35)", borderRadius:4, padding:"1px 7px", fontSize:10, color:brand.turquoise, fontWeight:700, letterSpacing:"0.08em", textTransform:"uppercase", marginLeft:8, verticalAlign:"middle" }}>
      Multilingual
    </span>
  );
}

function DualAudioPlayer({ uploadedBuffer, uploadedName, recordingBuffer, recordingDownloadUrl, referenceTranscript, recordingTranscript, isReferenceMultilingual = false, isRecordingMultilingual = false, displayReferenceTranscript = null, action = null }) {
  const refText = referenceTranscript;
  return (
    <div>
      <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:16 }}>
        <div style={{ ...S.sectionHd, marginBottom:0 }}>Audio Comparison</div>
        {action}
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
        <div>
          <AudioPlayer audioBuffer={uploadedBuffer} label={`Uploaded — ${uploadedName || "No file"}`} accent={brand.red} noAudioLabel="No file uploaded" />
          <div style={{ marginTop: 8 }}>
            <div style={{ fontSize: 10, color: brand.red, letterSpacing: "0.08em", textTransform: "uppercase", fontWeight: 700, marginBottom: 5 }}>
              Reference Transcript{isReferenceMultilingual && <MultilingualBadge />}
            </div>
            <p style={{ color: brand.offWhite, fontSize: 12, fontStyle: "italic", background: "rgba(0,0,0,0.2)", borderRadius: 6, padding: "10px 12px", margin: 0, lineHeight: 1.6 }}>"{refText}"</p>
          </div>
        </div>
        <div>
          <AudioPlayer audioBuffer={recordingBuffer} label="Call Recording" accent={brand.turquoise} noAudioLabel="No recording yet" />
          <div style={{ marginTop: 8 }}>
            <div style={{ fontSize: 10, color: brand.turquoise, letterSpacing: "0.08em", textTransform: "uppercase", fontWeight: 700, marginBottom: 5 }}>
              Recording Transcript{isRecordingMultilingual && <MultilingualBadge />}
            </div>
            <p style={{ color: brand.offWhite, fontSize: 12, fontStyle: "italic", background: "rgba(0,0,0,0.2)", borderRadius: 6, padding: "10px 12px", margin: 0, lineHeight: 1.6 }}>"{recordingTranscript}"</p>
          </div>
          {recordingDownloadUrl && (
            <div style={{ marginTop: 8, textAlign: "right" }}>
              <a href={recordingDownloadUrl} target="_blank" rel="noopener noreferrer"
                style={{ fontSize: 12, color: brand.turquoise, textDecoration: "none", fontWeight: 600, letterSpacing: "0.04em" }}>
                ↓ Download recording
              </a>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// API helpers
// ---------------------------------------------------------------------------
// Attaches a Cognito Bearer token to every fetch request when auth is active.
async function authFetch(url: string, options: RequestInit = {}): Promise<Response> {
  const token = await CognitoService.getAccessToken();
  if (CognitoService.isEnabled() && !token) {
    CognitoService.signOut();
    window.location.reload();
    return new Promise(() => {}); // never resolves — page is reloading
  }
  const headers: Record<string, string> = { ...(options.headers as Record<string, string> || {}) };
  if (token) headers["Authorization"] = `Bearer ${token}`;
  return fetch(url, { ...options, headers });
}

const API_BASE = `${process.env.REACT_APP_API_URL || ""}/api/sound-checks`;
async function apiStartCheck(client, phoneNumbers, email, audioFile, isMultilingual = false, caseUrl = "", programmedBy = "") {
  const form = new FormData();
  form.append("client", client);
  form.append("requesterEmail", email);
  phoneNumbers.forEach(n => form.append("phoneNumbers", n));
  if (audioFile) form.append("audioFile", audioFile);
  form.append("isMultilingual", String(isMultilingual));
  if (caseUrl) form.append("caseUrl", caseUrl);
  if (programmedBy) form.append("programmedBy", programmedBy);
  const res = await authFetch(`${API_BASE}/manual`, { method: "POST", body: form });
  if (!res.ok) throw new Error(`Start failed: ${res.status}`); return res.json();
}
async function apiScheduleCheck(client, phoneNumbers, email, scheduledAt, ianaTimezone, localScheduledAt, audioFile, isMultilingual = false, caseUrl = "", programmedBy = "") {
  const form = new FormData();
  form.append("client", client);
  form.append("requesterEmail", email);
  form.append("scheduledAt", scheduledAt);
  form.append("ianaTimezone", ianaTimezone);
  form.append("localScheduledAt", localScheduledAt);
  phoneNumbers.forEach(n => form.append("phoneNumbers", n));
  if (audioFile) form.append("audioFile", audioFile);
  form.append("isMultilingual", String(isMultilingual));
  if (caseUrl) form.append("caseUrl", caseUrl);
  if (programmedBy) form.append("programmedBy", programmedBy);
  const res = await authFetch(`${API_BASE}/schedule`, { method: "POST", body: form });
  if (!res.ok) throw new Error(`Schedule failed: ${res.status}`); return res.json();
}
async function apiUpdateScheduled(id, { scheduledAt, ianaTimezone, localScheduledAt, phoneNumber, audioFile, isMultilingual = null, caseUrl = null, client = null, programmedBy = null }) {
  const form = new FormData();
  if (scheduledAt)             form.append("scheduledAt", scheduledAt);
  if (ianaTimezone)            form.append("ianaTimezone", ianaTimezone);
  if (localScheduledAt)        form.append("localScheduledAt", localScheduledAt);
  if (phoneNumber)             form.append("phoneNumber", phoneNumber);
  if (audioFile)               form.append("audioFile", audioFile);
  if (isMultilingual !== null) form.append("isMultilingual", String(isMultilingual));
  if (caseUrl !== null)        form.append("caseUrl", caseUrl);
  if (client !== null)         form.append("client", client);
  if (programmedBy !== null)   form.append("programmedBy", programmedBy);
  const res = await authFetch(`${API_BASE}/scheduled/${id}`, { method: "PATCH", body: form });
  if (!res.ok) throw new Error(`Update failed: ${res.status}`);
}
async function apiGetStatus(id) {
  const res = await authFetch(`${API_BASE}/${id}`);
  if (!res.ok) throw new Error(`Poll failed: ${res.status}`); return res.json();
}
async function apiApproveSoundCheck(id, approvedBy, reason) {
  const res = await authFetch(`${API_BASE}/${id}/approve`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ approvedBy, reason }),
  });
  if (!res.ok) throw new Error(`Approve failed: ${res.status}`);
}
async function apiRevokeApproval(id) {
  const res = await authFetch(`${API_BASE}/${id}/approve`, { method: "DELETE" });
  if (!res.ok) throw new Error(`Revoke failed: ${res.status}`);
}
async function apiCanExport() {
  const email = await CognitoService.getCurrentUserEmail();
  if (!email) return false;
  const res = await authFetch(`${API_BASE}/export/allowed?requesterEmail=${encodeURIComponent(email)}`);
  if (!res.ok) return false;
  const data = await res.json();
  return !!data.allowed;
}
async function exportAllData() {
  const email = await CognitoService.getCurrentUserEmail();
  const res = await authFetch(`${API_BASE}/export/all?requesterEmail=${encodeURIComponent(email ?? "")}`);
  if (res.status === 403) throw new Error("You don't have permission to export this data.");
  if (!res.ok) throw new Error(`Export failed: ${res.status}`);
  const blob = await res.blob();
  const cd = res.headers.get("Content-Disposition");
  // Match the quoted form (stop at the closing ") or the unquoted token form (stop at ";" or
  // end of string) - a filename with no special characters is a valid HTTP token and comes back
  // unquoted, so relying on a closing quote alone swallows the rest of the header (including the
  // trailing filename*=... parameter) as part of the name.
  const filenameMatch = cd?.match(/filename="([^"]+)"|filename=([^;]+)/);
  const filename = (filenameMatch?.[1] ?? filenameMatch?.[2])?.trim() ?? "sound-check-export.xlsx";
  const objectUrl = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = objectUrl; a.download = filename;
  document.body.appendChild(a); a.click(); a.remove();
  URL.revokeObjectURL(objectUrl);
}
async function fetchRecordingBuffer(url) {
  const res = await fetch(url);
  if (!res.ok) throw new Error("Could not fetch recording");
  const ab = await res.arrayBuffer();
  const ctx = new ((window as any).AudioContext || (window as any).webkitAudioContext)();
  const decoded = await ctx.decodeAudioData(ab); await ctx.close(); return decoded;
}
async function pollUntilDone(id, onUpdate, onSlow?) {
  const deadline = Date.now() + 120_000;
  const slowThreshold = Date.now() + 60_000;
  let slowFired = false;
  while (Date.now() < deadline) {
    await new Promise(r => setTimeout(r, 3000));
    if (!slowFired && Date.now() > slowThreshold) { slowFired = true; onSlow?.(); }
    const data = await apiGetStatus(id); onUpdate(data.callResults || []);
    if ((data.callResults || []).every(r => r.status === "Completed" || r.status === "Failed" || r.status === "TranscriptionFailed")) return data;
  }
  throw new Error("Sound check timed out after 2 minutes. The call may still be in progress — check the History tab for results.");
}

// ---------------------------------------------------------------------------
// Audio utils
// ---------------------------------------------------------------------------
async function decodeFile(file) {
  const ab = await file.arrayBuffer();
  const ctx = new ((window as any).AudioContext || (window as any).webkitAudioContext)();
  const decoded = await ctx.decodeAudioData(ab); await ctx.close(); return decoded;
}

// ---------------------------------------------------------------------------
// Stat card
// ---------------------------------------------------------------------------
function StatCard({ label, value, accent }) {
  return (
    <div style={{ ...S.card, padding: 20, position: "relative", overflow: "hidden" }}>
      <div style={{ borderLeft: `3px solid ${accent || brand.red}`, paddingLeft: 12 }}>
        <div style={{ fontSize: 11, color: brand.muted, letterSpacing: "0.1em", textTransform: "uppercase", fontWeight: 600, marginBottom: 6 }}>{label}</div>
        <div style={{ fontSize: 32, fontWeight: 800, color: accent || brand.white }}>{value}</div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Dashboard
// ---------------------------------------------------------------------------
function Dashboard({ history, scheduled, correctTotal, incorrectTotal, failedTotal, scheduledTotal, onTabChange, onRefresh }) {
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState("");
  const [canExport, setCanExport] = useState(false);

  useEffect(() => { apiCanExport().then(setCanExport); }, []);

  const handleExport = async () => {
    setExporting(true);
    setExportError("");
    try {
      await exportAllData();
    } catch (e: any) {
      setExportError(e.message || "Export failed.");
    } finally {
      setExporting(false);
    }
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 24 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 12 }}>
        <div style={{ ...S.sectionHd, marginBottom: 0 }}>Overview</div>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          {exportError && <span style={{ color: brand.danger, fontSize: 12 }}>{exportError}</span>}
          {canExport && (
            <button onClick={handleExport} disabled={exporting} style={S.ghostBtn}>
              {exporting ? "Exporting…" : "Export All Data"}
            </button>
          )}
          <RefreshButton onRefresh={onRefresh} />
        </div>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 16 }}>
        <StatCard label="Total Checks" value={correctTotal + incorrectTotal + failedTotal + scheduledTotal} accent={brand.white} />
        <StatCard label="Correct" value={correctTotal} accent={brand.success} />
        <StatCard label="Incorrect" value={incorrectTotal} accent={brand.danger} />
        <StatCard label="Upcoming" value={scheduledTotal} accent={brand.turquoise} />
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
        {[
          { title: "Upcoming Scheduled", items: scheduled.slice(0,3), tabName: "Scheduled", sub: s => `${s.scheduledAt} · ${s.numbers.length} number${s.numbers.length>1?"s":""}` },
          { title: "Recent Results",     items: history.slice(0,3),   tabName: "History",   sub: h => `${h.completedAt} · Match: ${h.matchScore}%` },
        ].map(({ title, items, tabName, sub }) => (
          <div key={tabName} style={{ ...S.card, padding: 20 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
              <div style={S.sectionHd}>{title}</div>
              <button onClick={() => onTabChange(tabName)} style={{ ...S.ghostBtn, fontSize: 12, color: brand.red, border: "none", padding: "4px 0" }}>View all →</button>
            </div>
            <RedLine />
            <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
              {items.map(item => (
                <div key={item.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <div>
                    <div style={{ fontSize: 14, fontWeight: 600, color: brand.white, marginBottom: 2 }}>{item.client}</div>
                    <div style={{ fontSize: 12, color: brand.muted }}>{sub(item)}</div>
                  </div>
                  <Badge status={item.status} />
                </div>
              ))}
              {items.length === 0 && <p style={{ color: brand.mutedDark, fontSize: 13 }}>None yet</p>}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// New Check
// ---------------------------------------------------------------------------
function NewCheck({ onRefreshHistory, onRefreshScheduled, prefill }) {
  const [client, setClient] = useState(""); const [email, setEmail] = useState("");
  useEffect(() => { CognitoService.getCurrentUserEmail().then(e => { if (e) setEmail(e); }); }, []);

  useEffect(() => {
    if (!prefill) return;
    setClient(prefill.client || "");
    if (prefill.email) setEmail(prefill.email);
    setNumbers(prefill.numbers?.length ? prefill.numbers : [""]);
    setResult(null); setCallError(null);
    setUploadedBuffer(null); setUploadedFile(null); setUploadedName(null);
    setIsMultilingual(prefill.isMultilingual ?? false);
    setCaseUrl(prefill.caseUrl || "");
    setProgrammedBy((prefill.programmedBy || "").toLowerCase());
    setSchedTimezone(prefill.schedTimezone || "");
    if (prefill.schedTime) setSchedTime(prefill.schedTime);
    if (prefill.uploadedFileUrl) {
      fetch(prefill.uploadedFileUrl)
        .then(r => r.blob())
        .then(blob => handleFile(new File([blob], prefill.uploadedName || "reference.mp3", { type: blob.type || "audio/mpeg" })))
        .catch(() => {});
    }
  }, [prefill]);

  const [numbers, setNumbers] = useState([""]); const [uploadedBuffer, setUploadedBuffer] = useState(null);
  const [uploadedFile, setUploadedFile] = useState(null); const [uploadedName, setUploadedName] = useState(null); const [decoding, setDecoding] = useState(false);
  const [decodeErr, setDecodeErr] = useState(null); const [schedDate, setSchedDate] = useState("");
  const [schedTime, setSchedTime] = useState(""); const [schedTimezone, setSchedTimezone] = useState("");
  const [availableTimezones, setAvailableTimezones] = useState(ALL_TIMEZONES);
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null); const [callStatuses, setCallStatuses] = useState([]);
  const [callError, setCallError] = useState(null); const [pollSlow, setPollSlow] = useState(false);
  const [isMultilingual, setIsMultilingual] = useState(false);
  const [caseUrl, setCaseUrl] = useState("");
  const [programmedBy, setProgrammedBy] = useState("");

  const addNum = () => setNumbers([...numbers, ""]);
  const removeNum = i => setNumbers(numbers.filter((_,j) => j !== i));
  const updateNum = (i,v) => { const n=[...numbers]; n[i]=v; setNumbers(n); };

  useEffect(() => {
    const firstValid = numbers.find(n => n && isValidPhoneNumber(n));
    const zones = getTimezonesForPhone(firstValid || "");
    setAvailableTimezones(zones);
    setSchedTimezone(prev => {
      if (zones.length === 1) return zones[0];
      if (zones.includes(prev)) return prev;
      return "";
    });
  }, [numbers]);

  const [scheduleDuplicates, setScheduleDuplicates] = useState([]);
  const [scheduleChecking, setScheduleChecking]     = useState(false);
  const [showRevertPrompt, setShowRevertPrompt]     = useState(false);

  useEffect(() => { setScheduleDuplicates([]); }, [schedDate, numbers, uploadedName]);

  const handleFile = async (f) => {
    if (!f) return; setDecoding(true); setDecodeErr(null); setUploadedBuffer(null); setUploadedFile(null); setUploadedName(null);
    try { const buf = await decodeFile(f); setUploadedBuffer(buf); setUploadedFile(f); setUploadedName(f.name); }
    catch { setDecodeErr("Could not decode — ensure file is MP3, WAV, OGG or AAC."); }
    setDecoding(false);
  };

  const handleSubmit = async () => {
    const valid = numbers.filter(Boolean);
    if (!client || valid.length === 0 || valid.some(n => !isValidPhoneNumber(n)) || !uploadedFile) return;
    setRunning(true); setResult(null); setCallStatuses([]); setCallError(null); setPollSlow(false);
    let res = null;
    try {
      const startData = await apiStartCheck(client, valid, email, uploadedFile, isMultilingual, caseUrl, programmedBy);
      const checkId = startData.id;
      const finalData = await pollUntilDone(checkId, setCallStatuses, () => setPollSlow(true));
      const finals = finalData.callResults || [];
      const first = finals.find(r => r.status === "Completed" && r.recordingUrl);
      let recBuf = null;
      if (first) { try { recBuf = await fetchRecordingBuffer(first.recordingUrl); } catch(e) { console.warn(e); } }
      res = { ...mapDto(finalData), uploadedBuffer, uploadedName, recordingBuffer: recBuf };
      onRefreshHistory();
    } catch(err) {
      setCallError(err.message);
      res = { client, numbers: valid, email, matchScore: 0, status: "incorrect", transcript: "Check failed — could not complete call.", completedAt: new Date().toLocaleString("en-GB").replace(",",""), type: "manual", uploadedBuffer, uploadedName, recordingBuffer: null, callResults: [] };
    }
    setResult(res);
    setRunning(false);
  };

  const doSchedule = async (validNums) => {
    try {
      const utcIso = localToUtc(schedDate, schedTime, schedTimezone);
      const localIso = `${schedDate}T${schedTime}:00`;
      await apiScheduleCheck(client, validNums, email, utcIso, schedTimezone, localIso, uploadedFile, isMultilingual, caseUrl, programmedBy);
      onRefreshScheduled();
      setShowRevertPrompt(true);
    } catch(err) {
      console.error("Schedule failed:", err);
    }
    setSchedDate(""); setSchedTime(""); setSchedTimezone("");
    setScheduleDuplicates([]);
  };

  const handleScheduleRevert = () => {
    setUploadedBuffer(null); setUploadedFile(null); setUploadedName(null);
    setIsMultilingual(false);
    setShowRevertPrompt(false);
  };

  const handleSchedule = async () => {
    const validNums = numbers.filter(Boolean);
    if (!client || validNums.length === 0 || validNums.some(n => !isValidPhoneNumber(n)) || !schedDate || !schedTime || !schedTimezone || !uploadedFile) return;
    if (scheduleDuplicates.length > 0) { await doSchedule(validNums); return; }
    setScheduleChecking(true);
    try {
      const r = await authFetch(`${API_BASE}/scheduled?date=${schedDate}&pageSize=500`);
      const d = await r.json();
      const dupes = (d.items || []).map(mapDto).filter(s =>
        s.uploadedName === uploadedName && validNums.some(n => s.numbers.includes(n))
      );
      if (dupes.length > 0) { setScheduleDuplicates(dupes); }
      else { await doSchedule(validNums); }
    } catch { await doSchedule(validNums); }
    setScheduleChecking(false);
  };

  const inputFocus = e => e.target.style.borderColor = brand.red;
  const inputBlur  = e => e.target.style.borderColor = brand.tealBorder;

  return (
    <div style={{ maxWidth: 640, margin: "0 auto" }}>
      <div style={{ ...S.card, padding: 28 }}>
        <div style={S.sectionHd}>Sound Check Details</div>
        <RedLine />
        <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
          <div>
            <label style={S.label}>Client Name</label>
            <input value={client} onChange={e=>setClient(e.target.value)} placeholder="e.g. Acme Corp" style={S.inputBase} onFocus={inputFocus} onBlur={inputBlur} />
          </div>
          <div>
            <label style={S.label}>Phone Numbers</label>
            <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
              {numbers.map((n,i) => (
                <div key={i} style={{ display:"flex", gap:8, alignItems:"center" }}>
                  <div style={{ flex:1, backgroundColor: brand.teal, border: `1px solid ${n && !isValidPhoneNumber(n) ? brand.danger : brand.tealBorder}`, borderRadius:8, padding:"2px 8px", boxSizing:"border-box" }}>
                    <PhoneInput
                      flags={flags}
                      international
                      defaultCountry="GB"
                      value={n}
                      onChange={v => updateNum(i, v || "")}
                      style={{ color: brand.white, fontSize: 14 }}
                    />
                  </div>
                  {n && !isValidPhoneNumber(n) && <span style={{ fontSize:11, color:brand.danger, whiteSpace:"nowrap" }}>Invalid number</span>}
                  {numbers.length > 1 && <button onClick={()=>removeNum(i)} style={{ ...S.ghostBtn, color:brand.danger, borderColor:"rgba(235,23,0,0.3)", padding:"8px 14px" }}>✕</button>}
                </div>
              ))}
              <button onClick={addNum} style={{ ...S.ghostBtn, color:brand.turquoise, borderColor:`rgba(0,255,184,0.3)`, width:"fit-content", fontSize:13 }}>+ Add number</button>
            </div>
          </div>
          <div>
            <label style={S.label}>Comparison Audio File</label>
            <div onClick={() => document.getElementById("au").click()}
              style={{ border:`2px dashed ${uploadedBuffer ? brand.red : brand.muted}`, borderRadius:8, padding:"32px 20px", textAlign:"center", cursor:"pointer", background: uploadedBuffer?"rgba(235,23,0,0.05)":"rgba(0,0,0,0.15)", transition:"all 0.15s" }}
              onMouseEnter={e => { if (!uploadedBuffer) (e.currentTarget as HTMLDivElement).style.borderColor = brand.red; (e.currentTarget as HTMLDivElement).style.background = "rgba(235,23,0,0.04)"; }}
              onMouseLeave={e => { if (!uploadedBuffer) (e.currentTarget as HTMLDivElement).style.borderColor = brand.muted; (e.currentTarget as HTMLDivElement).style.background = uploadedBuffer ? "rgba(235,23,0,0.05)" : "rgba(0,0,0,0.15)"; }}>
              <input id="au" type="file" accept="audio/*" style={{ display:"none" }} onChange={e=>handleFile(e.target.files[0])} />
              {decoding
                ? <p style={{ color:brand.muted, fontSize:13, margin:0 }}>Decoding audio…</p>
                : uploadedBuffer
                  ? <p style={{ color:brand.red, fontSize:13, fontWeight:600, margin:0 }}>🎵 {uploadedName}</p>
                  : <>
                      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke={brand.muted} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ marginBottom:8, display:"block", margin:"0 auto 8px" }}>
                        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                        <polyline points="17 8 12 3 7 8"/>
                        <line x1="12" y1="3" x2="12" y2="15"/>
                      </svg>
                      <p style={{ color:brand.offWhite, fontSize:13, fontWeight:600, margin:"0 0 4px" }}>Click to upload audio file</p>
                      <p style={{ color:brand.mutedDark, fontSize:11, margin:0 }}>MP3, WAV, OGG or AAC</p>
                    </>}
            </div>
            {decodeErr && <p style={{ color:brand.danger, fontSize:12, marginTop:6 }}>{decodeErr}</p>}
            {uploadedBuffer && <div style={{ marginTop:12 }}><AudioPlayer audioBuffer={uploadedBuffer} label={`Preview — ${uploadedName}`} accent={brand.red} /></div>}
            {uploadedBuffer && (
              <label style={{ display:"flex", alignItems:"center", gap:8, marginTop:10, cursor:"pointer", userSelect:"none" as any }}>
                <input type="checkbox" checked={isMultilingual} onChange={e=>setIsMultilingual(e.target.checked)}
                  style={{ width:15, height:15, accentColor:brand.turquoise, cursor:"pointer" }} />
                <span style={{ fontSize:12, color:brand.muted }}>This reference audio contains multiple languages</span>
              </label>
            )}
          </div>
          <div>
            <label style={S.label}>Requester Email</label>
            <input value={email} onChange={e=>setEmail(e.target.value)} style={S.inputBase} onFocus={inputFocus} onBlur={inputBlur} />
          </div>
          <div>
            <label style={S.label}>Case URL <span style={{ color:brand.mutedDark, fontWeight:400, textTransform:"none" as any, letterSpacing:"normal" }}>(optional)</span></label>
            <input value={caseUrl} onChange={e=>setCaseUrl(e.target.value)} placeholder="e.g. https://crm.example.com/case/1234" style={S.inputBase} onFocus={inputFocus} onBlur={inputBlur} />
          </div>
          <div>
            <label style={S.label}>Delivery By <span style={{ color:brand.mutedDark, fontWeight:400, textTransform:"none" as any, letterSpacing:"normal" }}>(optional)</span></label>
            <select value={programmedBy} onChange={e=>setProgrammedBy(e.target.value)} style={S.inputBase} onFocus={inputFocus} onBlur={inputBlur}>
              <option value="">—</option>
              <option value="phmg">PHMG</option>
              <option value="client">CLIENT</option>
              <option value="maintainer">MAINTAINER</option>
            </select>
          </div>

          {/* Run now button */}
          <button onClick={handleSubmit} disabled={running||!client||!uploadedFile||numbers.filter(Boolean).some(n=>!isValidPhoneNumber(n))||numbers.every(n=>!n)}
            style={{ ...S.primaryBtn, opacity:running||!client||!uploadedFile||numbers.filter(Boolean).some(n=>!isValidPhoneNumber(n))||numbers.every(n=>!n)?0.5:1, cursor:running||!client||!uploadedFile||numbers.filter(Boolean).some(n=>!isValidPhoneNumber(n))||numbers.every(n=>!n)?"not-allowed":"pointer", display:"flex", alignItems:"center", justifyContent:"center", gap:8 }}>
            {running ? <><span style={{ display:"inline-block", animation:"spin 1s linear infinite" }}>⟳</span> Running…</> : "▶  Run Sound Check Now"}
          </button>
        </div>
      </div>

      {/* OR divider */}
      <div style={{ display:"flex", alignItems:"center", gap:12, margin:"20px 0" }}>
        <div style={{ flex:1, height:1, background:brand.tealBorder }} />
        <span style={{ fontSize:11, fontWeight:700, letterSpacing:"0.15em", color:brand.mutedDark }}>OR</span>
        <div style={{ flex:1, height:1, background:brand.tealBorder }} />
      </div>

      {/* Schedule card */}
      <div style={{ ...S.card, padding:28 }}>
        <div style={S.sectionHd}>Schedule This Check</div>
        <RedLine />
        <div style={{ display:"flex", flexDirection:"column", gap:12, marginBottom:16 }}>
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12 }}>
            <div><label style={S.label}>Date</label><input type="date" value={schedDate} onChange={e=>setSchedDate(e.target.value)} style={{ ...S.inputBase, colorScheme:"dark" }} onFocus={inputFocus} onBlur={inputBlur} /></div>
            <div><label style={S.label}>Time</label><input type="time" value={schedTime} onChange={e=>setSchedTime(e.target.value)} style={{ ...S.inputBase, colorScheme:"dark" }} onFocus={inputFocus} onBlur={inputBlur} /></div>
          </div>
          <div>
            <label style={S.label}>Timezone</label>
            <TimezoneSelect value={schedTimezone} onChange={setSchedTimezone} timezones={availableTimezones} />
          </div>
          {schedTimezone && schedDate && schedTime && (
            <div>
              <label style={S.label}>Our DateTime</label>
              <div style={{ ...S.inputBase, color: brand.muted, cursor: "default" }}>
                {formatLocalDateTime(localToUtc(schedDate, schedTime, schedTimezone))}
              </div>
            </div>
          )}
        </div>
        {scheduleDuplicates.length > 0 && (
          <div style={{ background:"rgba(255,191,0,0.08)", border:"1px solid rgba(255,191,0,0.35)", borderRadius:8, padding:"10px 14px", marginBottom:12, display:"flex", gap:10, alignItems:"flex-start" }}>
            <span style={{ fontSize:14, lineHeight:1 }}>⚠</span>
            <div style={{ fontSize:12, color:"#ffd966", lineHeight:1.5 }}>
              <strong>Possible duplicate</strong> — {scheduleDuplicates.length === 1 ? "a check" : `${scheduleDuplicates.length} checks`} already scheduled for this date with the same audio file and at least one matching number.
            </div>
          </div>
        )}
        <button onClick={handleSchedule} disabled={scheduleChecking||!client||!schedDate||!schedTime||!schedTimezone||!uploadedFile||numbers.filter(Boolean).some(n=>!isValidPhoneNumber(n))||numbers.every(n=>!n)}
          style={{
            width: "100%", padding: "11px 24px", fontSize: 13, fontWeight: 600,
            letterSpacing: "0.05em", textTransform: "uppercase", borderRadius: 8, cursor: "pointer",
            transition: "all 0.15s",
            ...(scheduleChecking||!client||!schedDate||!schedTime||!schedTimezone||!uploadedFile||numbers.filter(Boolean).some(n=>!isValidPhoneNumber(n))||numbers.every(n=>!n)
              ? { background: "transparent", border: `1px solid ${brand.tealBorder}`, color: brand.mutedDark, cursor: "not-allowed" }
              : scheduleDuplicates.length > 0
                ? { background: "transparent", border: `1px solid ${brand.danger}`, color: brand.danger }
                : { background: "transparent", border: `1px solid ${brand.turquoise}`, color: brand.turquoise })
          }}>
          {scheduleChecking ? "Checking…" : scheduleDuplicates.length > 0 ? "Add Anyway" : "Add to Schedule"}
        </button>
        {(!client||!schedDate||!schedTime||!schedTimezone||!uploadedFile||numbers.filter(Boolean).some(n=>!isValidPhoneNumber(n))||numbers.every(n=>!n)) && (
          <p style={{ fontSize: 11, color: brand.mutedDark, margin: "6px 0 0", textAlign: "center" }}>
            {!client ? "Enter a client name" : !uploadedFile ? "Upload a reference audio file" : numbers.every(n=>!n) ? "Enter at least one phone number" : numbers.filter(Boolean).some(n=>!isValidPhoneNumber(n)) ? "Fix invalid phone numbers" : !schedDate || !schedTime ? "Select a date and time" : !schedTimezone ? "Select a timezone" : ""} to schedule
          </p>
        )}
        {showRevertPrompt && (
          <div style={{ background:"rgba(0,255,184,0.08)", border:`1px solid rgba(0,255,184,0.35)`, borderRadius:8, padding:"10px 14px", marginTop:12, display:"flex", gap:10, alignItems:"flex-start" }}>
            <span style={{ fontSize:14, lineHeight:1 }}>✓</span>
            <div style={{ flex:1, fontSize:12, color:brand.turquoise, lineHeight:1.5 }}>
              <strong>Check scheduled.</strong> Is this a temporary change? Schedule a revert check for when it's expected to change back — client, number(s) and case URL are carried over below.
              <div style={{ marginTop:8 }}>
                <button onClick={handleScheduleRevert} style={{ ...S.ghostBtn, color:brand.turquoise, borderColor:"rgba(0,255,184,0.3)", fontSize:12, padding:"6px 12px" }}>Schedule Revert Check</button>
              </div>
            </div>
            <button onClick={()=>setShowRevertPrompt(false)} style={{ ...S.ghostBtn, color:brand.mutedDark, borderColor:brand.tealBorder, padding:"4px 10px", fontSize:11 }}>✕</button>
          </div>
        )}
      </div>

      {running && callStatuses.length > 0 && (
        <div style={{ ...S.card, padding:20, marginTop:14 }}>
          <div style={S.sectionHd}>Live Call Status</div>
          <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
            {callStatuses.map((s,i) => (
              <div key={i} style={{ display:"flex", justifyContent:"space-between", alignItems:"center" }}>
                <span style={{ color:brand.muted, fontSize:13 }}>{s.phoneNumber}</span>
                <Badge status={s.status} />
              </div>
            ))}
          </div>
          <p style={{ fontSize:11, color:brand.mutedDark, marginTop:10 }}>Polling every 3s — recordings may take up to 30s after the call ends.</p>
          {pollSlow && <p style={{ fontSize:12, color:brand.danger, marginTop:6 }}>⚠ Taking longer than expected — please wait, or check the History tab if this persists.</p>}
        </div>
      )}

      {callError && <div style={{ background:"rgba(235,23,0,0.1)", border:"1px solid rgba(235,23,0,0.3)", borderRadius:10, padding:16, marginTop:14, color:brand.danger, fontSize:13 }}>⚠️ {callError}</div>}

      {result && (
        <div style={{ border:`1px solid ${result.status==="correct"?"rgba(0,255,184,0.3)":"rgba(235,23,0,0.3)"}`, borderRadius:12, padding:24, marginTop:16, background:result.status==="correct"?"rgba(0,255,184,0.05)":"rgba(235,23,0,0.05)" }}>
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:16 }}>
            <div style={S.sectionHd}>Check Complete</div>
            <Badge status={result.status} />
          </div>
          <RedLine />
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12, marginBottom:20 }}>
            <div style={{ background:"rgba(0,0,0,0.2)", borderRadius:8, padding:16 }}>
              <div style={{ fontSize:11, color:brand.muted, marginBottom:4, letterSpacing:"0.08em", textTransform:"uppercase" }}>Match Score</div>
              <div style={{ fontSize:30, fontWeight:800, color:result.status==="correct"?brand.success:brand.danger }}>{result.matchScore}%</div>
            </div>
            <div style={{ background:"rgba(0,0,0,0.2)", borderRadius:8, padding:16 }}>
              <div style={{ fontSize:11, color:brand.muted, marginBottom:4, letterSpacing:"0.08em", textTransform:"uppercase" }}>Numbers Called</div>
              <div style={{ fontSize:30, fontWeight:800, color:brand.white }}>{result.numbers.length}</div>
            </div>
          </div>
          <DualAudioPlayer uploadedBuffer={result.uploadedBuffer} uploadedName={result.uploadedName} recordingBuffer={result.recordingBuffer} recordingDownloadUrl={result.recordingDownloadUrl} referenceTranscript={result.referenceTranscript} recordingTranscript={result.recordingTranscript} isReferenceMultilingual={result.isMultilingualReference} isRecordingMultilingual={result.isRecordingMultilingual} displayReferenceTranscript={result.displayReferenceTranscript} />
          <div style={{ background:"rgba(0,0,0,0.2)", borderRadius:8, padding:16, marginTop:16 }}>
            <div style={{ fontSize:11, color:brand.muted, marginBottom:6, letterSpacing:"0.08em", textTransform:"uppercase" }}>Transcript</div>
            <p style={{ color:brand.offWhite, fontSize:13, fontStyle:"italic", margin:0 }}>"{result.transcript}"</p>
          </div>
          <p style={{ fontSize:11, color:brand.mutedDark, marginTop:12 }}>Notification sent to {result.email}</p>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Pagination
// ---------------------------------------------------------------------------
function Pagination({ page, total, pageSize, onPage }) {
  const maxPage = Math.max(1, Math.ceil(total / pageSize));
  if (total <= pageSize) return null;
  return (
    <div style={{ display:"flex", alignItems:"center", gap:12, marginTop:16, justifyContent:"center" }}>
      <button onClick={() => onPage(page - 1)} disabled={page <= 1}
        style={{ ...S.ghostBtn, fontSize:12, opacity:page<=1?0.4:1, cursor:page<=1?"default":"pointer" }}>
        ← Previous
      </button>
      <span style={{ color:brand.muted, fontSize:13 }}>Page {page} of {maxPage}</span>
      <button onClick={() => onPage(page + 1)} disabled={page >= maxPage}
        style={{ ...S.ghostBtn, fontSize:12, opacity:page>=maxPage?0.4:1, cursor:page>=maxPage?"default":"pointer" }}>
        Next →
      </button>
    </div>
  );
}

// Scheduled
// ---------------------------------------------------------------------------
function ScheduledItem({ s, onRefresh }) {
  const [editing, setEditing]               = useState(false);
  const [editClient, setEditClient]         = useState("");
  const [editDate, setEditDate]             = useState("");
  const [editTime, setEditTime]             = useState("");
  const [editTimezone, setEditTimezone]     = useState("");
  const [editPhone, setEditPhone]           = useState("");
  const [editFile, setEditFile]             = useState(null);
  const [editIsMultilingual, setEditIsMultilingual] = useState(false);
  const [editCaseUrl, setEditCaseUrl]       = useState("");
  const [editProgrammedBy, setEditProgrammedBy] = useState("");
  const [saving, setSaving]                 = useState(false);
  const skipTimezoneReset                   = useRef(false);

  const editAvailableTimezones = editPhone && isValidPhoneNumber(editPhone)
    ? getTimezonesForPhone(editPhone)
    : ALL_TIMEZONES;

  useEffect(() => {
    if (!editing) return;
    if (skipTimezoneReset.current) {
      skipTimezoneReset.current = false;
      return;
    }
    const zones = editPhone && isValidPhoneNumber(editPhone) ? getTimezonesForPhone(editPhone) : ALL_TIMEZONES;
    setEditTimezone(prev => {
      if (zones.length === 1) return zones[0];
      if (zones.includes(prev)) return prev;
      return "";
    });
  }, [editPhone]);

  const startEdit = () => {
    setEditClient(s.client || "");
    if (s.localScheduledAt) {
      const parts = parseLocalDateTimeParts(s.localScheduledAt);
      if (parts) { setEditDate(parts.date); setEditTime(parts.time); }
    } else if (s.scheduledAtIso) {
      const d = new Date(s.scheduledAtIso);
      const pad = (n: number) => String(n).padStart(2, "0");
      setEditDate(`${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`);
      setEditTime(`${pad(d.getHours())}:${pad(d.getMinutes())}`);
    }
    skipTimezoneReset.current = true;
    setEditTimezone(s.ianaTimezone || "");
    setEditPhone(s.numbers[0] || "");
    setEditFile(null);
    setEditIsMultilingual(false);
    setEditCaseUrl(s.caseUrl || "");
    setEditProgrammedBy((s.programmedBy || "").toLowerCase());
    setEditing(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const scheduledAt    = editDate && editTime && editTimezone ? localToUtc(editDate, editTime, editTimezone) : undefined;
      const localSchedAt   = editDate && editTime ? `${editDate}T${editTime}:00` : undefined;
      await apiUpdateScheduled(s.id, { scheduledAt, ianaTimezone: editTimezone || undefined, localScheduledAt: localSchedAt, phoneNumber: editPhone || undefined, audioFile: editFile || undefined, isMultilingual: editFile ? editIsMultilingual : null, caseUrl: editCaseUrl, client: editClient, programmedBy: editProgrammedBy });
      setEditing(false);
      onRefresh();
    } catch (err) {
      console.error("Update failed:", err);
    } finally {
      setSaving(false);
    }
  };

  const inputFocus = e => e.target.style.borderColor = brand.red;
  const inputBlur  = e => e.target.style.borderColor = brand.tealBorder;
  const invalid    = !editClient.trim() || !editDate || !editTime || !editTimezone || !editPhone || (!!editPhone && !isValidPhoneNumber(editPhone));

  return (
    <div style={{ ...S.card, overflow:"hidden" }}>
      <div style={{ padding:20, display:"flex", justifyContent:"space-between", alignItems:"flex-start", gap:16 }}>
        <div style={{ flex:1 }}>
          <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:12 }}>
            <span style={{ fontSize:15, fontWeight:700, color:brand.white }}>{s.client}</span>
            <Badge status={s.status} />
          </div>
          <div style={{ display:"flex", gap:24, flexWrap:"wrap", alignItems:"flex-start" }}>
            <div>
              <div style={{ fontSize:10, color:brand.white, letterSpacing:"0.1em", textTransform:"uppercase", fontWeight:600, marginBottom:4 }}>Scheduled For Client Time</div>
              <div style={{ fontSize:13, color:brand.muted }}>{s.scheduledAt}</div>
            </div>
            {s.scheduledAtIso && (
              <div>
                <div style={{ fontSize:10, color:brand.white, letterSpacing:"0.1em", textTransform:"uppercase", fontWeight:600, marginBottom:4 }}>Scheduled For Our Time</div>
                <div style={{ fontSize:13, color:brand.muted }}>{formatLocalDateTime(s.scheduledAtIso)}</div>
              </div>
            )}
            {s.ianaTimezone && (
              <div>
                <div style={{ fontSize:10, color:brand.white, letterSpacing:"0.1em", textTransform:"uppercase", fontWeight:600, marginBottom:4 }}>Location</div>
                <div style={{ fontSize:13, color:brand.muted }}>{formatTimezone(s.ianaTimezone)}</div>
              </div>
            )}
            <div>
              <div style={{ fontSize:10, color:brand.white, letterSpacing:"0.1em", textTransform:"uppercase", fontWeight:600, marginBottom:4 }}>Number Dialled</div>
              <div style={{ display:"flex", flexWrap:"wrap", gap:4 }}>
                {s.numbers.map((n,i) => <span key={i} style={{ background:`rgba(0,255,184,0.1)`, border:`1px solid rgba(0,255,184,0.2)`, borderRadius:4, padding:"2px 8px", fontSize:12, color:brand.turquoise }}>{n}</span>)}
              </div>
            </div>
            {s.uploadedName && (
              <div>
                <div style={{ fontSize:10, color:brand.white, letterSpacing:"0.1em", textTransform:"uppercase", fontWeight:600, marginBottom:4 }}>Audio File</div>
                <div style={{ fontSize:13, color:brand.muted }}>♪ {s.uploadedName}</div>
              </div>
            )}
            <div>
              <div style={{ fontSize:10, color:brand.white, letterSpacing:"0.1em", textTransform:"uppercase", fontWeight:600, marginBottom:4 }}>Contact</div>
              <div style={{ fontSize:13, color:brand.muted }}>✉ {s.email}</div>
            </div>
            {s.caseUrl && (
              <div>
                <div style={{ fontSize:10, color:brand.white, letterSpacing:"0.1em", textTransform:"uppercase", fontWeight:600, marginBottom:4 }}>Case URL</div>
                <div style={{ fontSize:13 }}><a href={s.caseUrl} target="_blank" rel="noopener noreferrer" style={{ color:brand.turquoise }}>{s.caseUrl}</a></div>
              </div>
            )}
            {s.programmedBy && (
              <div>
                <div style={{ fontSize:10, color:brand.white, letterSpacing:"0.1em", textTransform:"uppercase", fontWeight:600, marginBottom:4 }}>Delivery By</div>
                <div style={{ fontSize:13, color:brand.muted }}>{PROGRAMMED_BY_LABELS[s.programmedBy] ?? s.programmedBy}</div>
              </div>
            )}
          </div>
        </div>
        <div style={{ display:"flex", gap:8, flexShrink:0 }}>
          {!editing && <button onClick={startEdit} style={{ ...S.ghostBtn, color:brand.turquoise, borderColor:"rgba(0,255,184,0.3)" }}>Edit</button>}
          {!editing && <button onClick={async () => { await authFetch(`${API_BASE}/${s.id}`, { method:"DELETE" }); onRefresh(); }} style={{ ...S.ghostBtn, color:brand.danger, borderColor:"rgba(235,23,0,0.3)" }}>Cancel</button>}
        </div>
      </div>
      {editing && (
        <div style={{ borderTop:`1px solid ${brand.tealBorder}`, padding:20, background:"rgba(0,0,0,0.15)", display:"flex", flexDirection:"column", gap:16 }}>
          <div style={{ ...S.sectionHd, marginBottom:0 }}>Edit Check</div>
          <div>
            <label style={S.label}>Company Name</label>
            <input value={editClient} onChange={e=>setEditClient(e.target.value)} style={S.inputBase} onFocus={inputFocus} onBlur={inputBlur} />
          </div>
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12 }}>
            <div><label style={S.label}>Date</label><input type="date" value={editDate} onChange={e=>setEditDate(e.target.value)} style={{ ...S.inputBase, colorScheme:"dark" }} onFocus={inputFocus} onBlur={inputBlur} /></div>
            <div><label style={S.label}>Time</label><input type="time" value={editTime} onChange={e=>setEditTime(e.target.value)} style={{ ...S.inputBase, colorScheme:"dark" }} onFocus={inputFocus} onBlur={inputBlur} /></div>
          </div>
          <div>
            <label style={S.label}>Timezone</label>
            <TimezoneSelect value={editTimezone} onChange={setEditTimezone} timezones={editAvailableTimezones} />
            {!s.ianaTimezone && <p style={{ fontSize:11, color:brand.mutedDark, margin:"4px 0 0" }}>This check was created before timezone support — please select a timezone to save.</p>}
          </div>
          <div>
            <label style={S.label}>Phone Number</label>
            <div style={{ backgroundColor:brand.teal, border:`1px solid ${editPhone && !isValidPhoneNumber(editPhone) ? brand.danger : brand.tealBorder}`, borderRadius:8, padding:"2px 8px" }}>
              <PhoneInput flags={flags} international defaultCountry="GB" value={editPhone} onChange={v=>setEditPhone(v||"")} style={{ color:brand.white, fontSize:14 }} />
            </div>
            {editPhone && !isValidPhoneNumber(editPhone) && <span style={{ fontSize:11, color:brand.danger }}>Invalid number</span>}
          </div>
          <div>
            <label style={S.label}>Reference Audio (optional — leave blank to keep current)</label>
            <div onClick={() => document.getElementById(`edit-au-${s.id}`).click()}
              style={{ border:`2px dashed ${editFile ? brand.red : brand.muted}`, borderRadius:8, padding:"16px 20px", textAlign:"center", cursor:"pointer", background:editFile?"rgba(235,23,0,0.05)":"rgba(0,0,0,0.15)" }}>
              <input id={`edit-au-${s.id}`} type="file" accept="audio/*" style={{ display:"none" }} onChange={e=>{ setEditFile(e.target.files[0]||null); setEditIsMultilingual(false); }} />
              {editFile
                ? <p style={{ color:brand.red, fontSize:13, fontWeight:600, margin:0 }}>🎵 {editFile.name}</p>
                : <p style={{ color:brand.muted, fontSize:13, margin:0 }}>Click to replace — current: {s.uploadedName||"none"}</p>}
            </div>
            {editFile && (
              <label style={{ display:"flex", alignItems:"center", gap:8, marginTop:8, cursor:"pointer", userSelect:"none" as any }}>
                <input type="checkbox" checked={editIsMultilingual} onChange={e=>setEditIsMultilingual(e.target.checked)}
                  style={{ width:15, height:15, accentColor:brand.turquoise, cursor:"pointer" }} />
                <span style={{ fontSize:12, color:brand.muted }}>This reference audio contains multiple languages</span>
              </label>
            )}
          </div>
          <div>
            <label style={S.label}>Case URL <span style={{ color:brand.mutedDark, fontWeight:400, textTransform:"none" as any, letterSpacing:"normal" }}>(optional)</span></label>
            <input value={editCaseUrl} onChange={e=>setEditCaseUrl(e.target.value)} placeholder="e.g. https://crm.example.com/case/1234" style={S.inputBase} onFocus={inputFocus} onBlur={inputBlur} />
          </div>
          <div>
            <label style={S.label}>Delivery By <span style={{ color:brand.mutedDark, fontWeight:400, textTransform:"none" as any, letterSpacing:"normal" }}>(optional)</span></label>
            <select value={editProgrammedBy} onChange={e=>setEditProgrammedBy(e.target.value)} style={S.inputBase} onFocus={inputFocus} onBlur={inputBlur}>
              <option value="">—</option>
              <option value="phmg">PHMG</option>
              <option value="client">CLIENT</option>
              <option value="maintainer">MAINTAINER</option>
            </select>
          </div>
          <div style={{ display:"flex", gap:10 }}>
            <button onClick={handleSave} disabled={saving||invalid}
              style={{ ...S.primaryBtn, flex:1, opacity:saving||invalid?0.5:1, cursor:saving||invalid?"not-allowed":"pointer" }}>
              {saving ? "Saving…" : "Save Changes"}
            </button>
            <button onClick={()=>setEditing(false)} style={{ ...S.ghostBtn, flex:1, padding:"12px 24px" }}>Discard</button>
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Scheduled filter rail — filter state, rail component
// ---------------------------------------------------------------------------
interface ScheduledFilters {
  dateFrom: string;
  dateTo: string;
  programmedBy: "all" | "phmg" | "client" | "maintainer";
}

const defaultScheduledFilters: ScheduledFilters = {
  dateFrom: "", dateTo: "", programmedBy: "all",
};

type ScheduledFiltersAction =
  | { type: "SET_DATE_FIELD"; field: "dateFrom" | "dateTo"; value: string }
  | { type: "SET_PROGRAMMED_BY"; programmedBy: ScheduledFilters["programmedBy"] }
  | { type: "RESET" };

function scheduledFiltersReducer(state: ScheduledFilters, action: ScheduledFiltersAction): ScheduledFilters {
  switch (action.type) {
    case "SET_DATE_FIELD": {
      let { dateFrom, dateTo } = state;
      if (action.field === "dateFrom") {
        dateFrom = action.value;
        if (dateTo && dateFrom > dateTo) dateTo = dateFrom;
      } else {
        dateTo = action.value;
        if (dateFrom && dateTo < dateFrom) dateFrom = dateTo;
      }
      return { ...state, dateFrom, dateTo };
    }
    case "SET_PROGRAMMED_BY":
      return { ...state, programmedBy: action.programmedBy };
    case "RESET":
      return { ...defaultScheduledFilters };
    default:
      return state;
  }
}

function activeScheduledFilterCount(f: ScheduledFilters): number {
  let n = 0;
  if (f.dateFrom || f.dateTo) n++;
  if (f.programmedBy !== "all") n++;
  return n;
}

type ScheduledFilterSectionKey = "date" | "programmedBy";

function ScheduledFilterRail({ filters, dispatch, totalShowing, totalMatching, variant = "rail", onClose }: {
  filters: ScheduledFilters;
  dispatch: (action: ScheduledFiltersAction) => void;
  totalShowing: number;
  totalMatching: number;
  variant?: "rail" | "drawer";
  onClose?: () => void;
}) {
  const [openSections, setOpenSections] = useState<Record<ScheduledFilterSectionKey, boolean>>({
    date: true, programmedBy: true,
  });
  const toggleSection = (key: ScheduledFilterSectionKey) =>
    setOpenSections(s => ({ ...s, [key]: !s[key] }));

  const programmedByRow = (value: ScheduledFilters["programmedBy"], label: string) => {
    const active = filters.programmedBy === value;
    return (
      <div
        key={value}
        role="radio"
        aria-checked={active}
        tabIndex={0}
        onClick={() => dispatch({ type: "SET_PROGRAMMED_BY", programmedBy: value })}
        onKeyDown={e => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); dispatch({ type: "SET_PROGRAMMED_BY", programmedBy: value }); } }}
        style={{
          ...S.ghostBtn,
          display: "flex", alignItems: "center",
          width: "100%", height: 34, boxSizing: "border-box", marginBottom: 6, cursor: "pointer",
          background: active ? `linear-gradient(135deg, ${brand.red}, ${brand.pink})` : "transparent",
          color: active ? brand.white : brand.muted,
          borderColor: active ? "transparent" : brand.tealBorder,
          fontWeight: active ? 700 : 500,
        }}
      >
        <span>{label}</span>
      </div>
    );
  };

  const invalidRange = !!(filters.dateFrom && filters.dateTo && filters.dateTo < filters.dateFrom);

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%", padding: "20px 16px", boxSizing: "border-box" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
        <div style={{ fontSize: 11, color: brand.turquoise, letterSpacing: "0.12em", textTransform: "uppercase", fontWeight: 700 }}>Filters</div>
        <button onClick={() => dispatch({ type: "RESET" })}
          style={{ background: "none", border: "none", color: brand.muted, fontSize: 12, cursor: "pointer", textDecoration: "underline", padding: 0 }}>
          Reset
        </button>
      </div>

      <div className="sc-scroll" style={{ flex: 1, overflowY: "auto", overflowX: "hidden" }}>
        <FilterSection
          title="Date Scheduled"
          open={openSections.date}
          active={!!(filters.dateFrom || filters.dateTo)}
          onToggle={() => toggleSection("date")}
        >
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            <div>
              <div style={{ fontSize: 10, color: brand.muted, marginBottom: 4, letterSpacing: "0.06em", textTransform: "uppercase" }}>From</div>
              <input
                type="date"
                aria-label="From date"
                value={filters.dateFrom}
                onChange={e => dispatch({ type: "SET_DATE_FIELD", field: "dateFrom", value: e.target.value })}
                style={{ ...S.inputBase, colorScheme: "dark", padding: "6px 8px", fontSize: 12, width: "100%", boxSizing: "border-box", borderColor: invalidRange ? brand.danger : brand.tealBorder }}
              />
            </div>
            <div>
              <div style={{ fontSize: 10, color: brand.muted, marginBottom: 4, letterSpacing: "0.06em", textTransform: "uppercase" }}>To</div>
              <input
                type="date"
                aria-label="To date"
                value={filters.dateTo}
                onChange={e => dispatch({ type: "SET_DATE_FIELD", field: "dateTo", value: e.target.value })}
                style={{ ...S.inputBase, colorScheme: "dark", padding: "6px 8px", fontSize: 12, width: "100%", boxSizing: "border-box", borderColor: invalidRange ? brand.danger : brand.tealBorder }}
              />
            </div>
          </div>
        </FilterSection>

        <FilterSection
          title="Delivery By"
          open={openSections.programmedBy}
          active={filters.programmedBy !== "all"}
          onToggle={() => toggleSection("programmedBy")}
        >
          <div role="radiogroup" aria-label="Delivery By">
            {programmedByRow("all", "All checks")}
            {programmedByRow("phmg", "PHMG")}
            {programmedByRow("client", "Client")}
            {programmedByRow("maintainer", "Maintainer")}
          </div>
        </FilterSection>
      </div>

      <div style={{ borderTop: `1px solid ${brand.tealBorder}`, paddingTop: 12, fontSize: 12, color: brand.muted }}>
        Showing {totalShowing} of {totalMatching}
      </div>

      {variant === "drawer" && onClose && (
        <button onClick={onClose} style={{ ...S.ghostBtn, marginTop: 12 }}>Close</button>
      )}
    </div>
  );
}

function ScheduledList({ scheduled, onRefresh, page, total, pageSize, onPage, search, onSearch, filters, dispatch, loading }: {
  scheduled: any[]; onRefresh: () => void; page: number; total: number; pageSize: number; onPage: (p: number) => void;
  search: string; onSearch: (s: string) => void;
  filters: ScheduledFilters; dispatch: (action: ScheduledFiltersAction) => void;
  loading: boolean;
}) {
  const isNarrow = useMediaQuery("(max-width: 1100px)");
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [inputValue, setInputValue] = useState(search);
  const drawerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const t = setTimeout(() => { if (inputValue !== search) onSearch(inputValue); }, 500);
    return () => clearTimeout(t);
  }, [inputValue]);

  useEffect(() => { setInputValue(search); }, [search]);

  useEffect(() => {
    if (!drawerOpen) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") { setDrawerOpen(false); return; }
      if (e.key !== "Tab" || !drawerRef.current) return;
      const focusables = drawerRef.current.querySelectorAll<HTMLElement>('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])');
      if (focusables.length === 0) return;
      const first = focusables[0], last = focusables[focusables.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [drawerOpen]);

  const rail = (
    <ScheduledFilterRail
      filters={filters} dispatch={dispatch}
      totalShowing={scheduled.length} totalMatching={total}
      variant={isNarrow ? "drawer" : "rail"}
      onClose={isNarrow ? () => setDrawerOpen(false) : undefined}
    />
  );

  const activeCount = activeScheduledFilterCount(filters);

  const content = (
    <div style={{ position:"relative" }}>
      <style>{`@keyframes sc-spin { to { transform: rotate(360deg); } }`}</style>
      {loading && (
        <div style={{ position:"absolute", inset:0, background:"rgba(0,36,46,0.7)", borderRadius:8, display:"flex", alignItems:"flex-start", justifyContent:"center", paddingTop:80, zIndex:10 }}>
          <div style={{ width:36, height:36, border:`3px solid ${brand.tealBorder}`, borderTopColor:brand.turquoise, borderRadius:"50%", animation:"sc-spin 0.7s linear infinite" }} />
        </div>
      )}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16, flexWrap:"wrap", gap:12 }}>
        <div style={{ display:"flex", alignItems:"baseline", gap:10, flexWrap:"wrap" }}>
          <div style={{ ...S.sectionHd, marginBottom: 0 }}>Scheduled Sound Checks</div>
          <span style={{ color:brand.muted, fontSize:12 }}>{total} scheduled check{total === 1 ? "" : "s"}{activeCount > 0 ? " matching filter" : ""}</span>
        </div>
        <div style={{ display:"flex", gap:6, alignItems:"center", flexWrap:"wrap" }}>
          {isNarrow && (
            <button onClick={() => setDrawerOpen(true)} style={{ ...S.ghostBtn, display: "flex", alignItems: "center", gap: 6 }}>
              Filters
              {activeCount > 0 && (
                <span style={{ background: brand.red, color: brand.white, borderRadius: 10, padding: "1px 6px", fontSize: 10, fontWeight: 700 }}>
                  {activeCount}
                </span>
              )}
            </button>
          )}
          <input
            type="text"
            placeholder="Search client, contact or phone…"
            value={inputValue}
            onChange={e => setInputValue(e.target.value)}
            style={{ background:brand.tealLight, border:`1px solid ${brand.tealBorder}`, borderRadius:7, padding:"6px 12px", color:brand.white, fontSize:12, outline:"none", width:200 }}
          />
          <RefreshButton onRefresh={onRefresh} />
        </div>
      </div>
      <RedLine />
      {scheduled.length===0 && <p style={{ color:brand.mutedDark }}>No scheduled checks.</p>}
      <div style={{ display:"flex", flexDirection:"column", gap:12 }}>
        {scheduled.map(s => <ScheduledItem key={s.id} s={s} onRefresh={onRefresh} />)}
      </div>
      <Pagination page={page} total={total} pageSize={pageSize} onPage={onPage} />
    </div>
  );

  if (isNarrow) {
    return (
      <div>
        {content}
        {drawerOpen && (
          <div style={{ position:"fixed", inset:0, zIndex:100, display:"flex" }}>
            <div onClick={() => setDrawerOpen(false)} style={{ position:"absolute", inset:0, background:"rgba(0,0,0,0.6)" }} />
            <div ref={drawerRef} className="sc-scroll" style={{ position:"relative", width:280, height:"100%", background:brand.tealLight, borderRight:`1px solid ${brand.tealBorder}`, overflowY:"auto", overflowX:"hidden" }}>
              {rail}
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div style={{ display:"grid", gridTemplateColumns:"268px 1fr", gap:0 }}>
      <div className="sc-scroll" style={{ borderRight:`1px solid ${brand.tealBorder}`, position:"sticky", top:64, alignSelf:"start", height:"calc(100vh - 64px)", overflowY:"auto", overflowX:"hidden" }}>
        {rail}
      </div>
      <div style={{ paddingLeft:20 }}>
        {content}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// History
// ---------------------------------------------------------------------------
function ManuallyApprovedBadge({ approvedBy, approvedAt, reason }) {
  const [hovered, setHovered] = useState(false);
  return (
    <span style={{ position:"relative", display:"inline-flex", alignItems:"center" }}
      onMouseEnter={() => setHovered(true)} onMouseLeave={() => setHovered(false)}>
      <span style={{ background:"rgba(255,191,0,0.15)", color:"#ffd966", border:"1px solid rgba(255,191,0,0.4)", borderRadius:20, padding:"2px 10px", fontSize:11, fontWeight:700, letterSpacing:"0.06em", textTransform:"uppercase", cursor:"default" }}>
        Manually approved
      </span>
      {hovered && (
        <div style={{ position:"absolute", bottom:"calc(100% + 6px)", left:0, zIndex:100, background:"#001A22", border:`1px solid ${brand.tealBorder}`, borderRadius:8, padding:"10px 14px", minWidth:240, maxWidth:320, boxShadow:"0 4px 16px rgba(0,0,0,0.5)", pointerEvents:"none" }}>
          {approvedBy && <p style={{ fontSize:12, color:brand.muted, margin:"0 0 4px" }}>By: <span style={{ color:brand.white }}>{approvedBy}</span></p>}
          {approvedAt && <p style={{ fontSize:12, color:brand.muted, margin:"0 0 4px" }}>At: <span style={{ color:brand.white }}>{approvedAt}</span></p>}
          {reason     && <p style={{ fontSize:12, color:brand.muted, margin:0 }}>Reason: <span style={{ color:brand.offWhite, fontStyle:"italic" }}>{reason}</span></p>}
        </div>
      )}
    </span>
  );
}

function HistoryItem({ h, onRerun, onRefresh }) {
  const [open, setOpen] = useState(false);
  const [recBuf, setRecBuf] = useState(null);
  const [uploadBuf, setUploadBuf] = useState(null);

  const [approveOpen, setApproveOpen]       = useState(false);
  const [approveReason, setApproveReason]   = useState("");
  const [approving, setApproving]           = useState(false);
  const [approveError, setApproveError]     = useState(null);

  const [revokeOpen, setRevokeOpen]         = useState(false);
  const [revoking, setRevoking]             = useState(false);
  const [revokeError, setRevokeError]       = useState(null);

  const canRerun = ["failed", "incorrect", "transcription-error"].includes(h.status);
  const canApprove = h.status === "incorrect";
  const firstWithRecording = (h.callResults || []).find(r => r.recordingUrl);

  useEffect(() => {
    if (!open) return;
    if (!recBuf && !h.recordingBuffer && firstWithRecording)
      fetchRecordingBuffer(firstWithRecording.recordingUrl).then(setRecBuf).catch(() => {});
    if (!uploadBuf && !h.uploadedBuffer && h.uploadedFileUrl)
      fetchRecordingBuffer(h.uploadedFileUrl).then(setUploadBuf).catch(() => {});
  }, [open]);

  const recording = h.recordingBuffer || recBuf;
  const uploaded  = h.uploadedBuffer  || uploadBuf;

  const handleApprove = async () => {
    if (!approveReason.trim()) return;
    setApproving(true); setApproveError(null);
    try {
      const email = await CognitoService.getCurrentUserEmail() || h.email;
      await apiApproveSoundCheck(h.id, email, approveReason.trim());
      setApproveOpen(false); setApproveReason("");
      onRefresh();
    } catch (err) {
      setApproveError("Failed to save approval. Please try again.");
    } finally {
      setApproving(false);
    }
  };

  const handleRevoke = async () => {
    setRevoking(true); setRevokeError(null);
    try {
      await apiRevokeApproval(h.id);
      setRevokeOpen(false);
      onRefresh();
    } catch (err) {
      setRevokeError("Failed to revoke approval. Please try again.");
    } finally {
      setRevoking(false);
    }
  };

  return (
    <>
      {/* Approve modal */}
      {approveOpen && (
        <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.7)", zIndex:200, display:"flex", alignItems:"center", justifyContent:"center" }}
          onClick={() => { if (!approving) { setApproveOpen(false); setApproveReason(""); setApproveError(null); } }}>
          <div style={{ background:"#001A22", border:`1px solid ${brand.tealBorder}`, borderRadius:12, padding:28, width:420, maxWidth:"90vw" }}
            onClick={e => e.stopPropagation()}>
            <div style={{ fontSize:15, fontWeight:700, color:brand.white, marginBottom:6 }}>Mark as Correct</div>
            <p style={{ fontSize:13, color:brand.muted, marginTop:0, marginBottom:16 }}>
              This will override the failed score for <strong style={{ color:brand.white }}>{h.client}</strong>. Please provide a reason.
            </p>
            <label style={S.label}>Reason (required)</label>
            <textarea
              value={approveReason}
              onChange={e => setApproveReason(e.target.value)}
              rows={3}
              placeholder="e.g. Transcription error — audio was verified correct by ear"
              style={{ ...S.inputBase, resize:"vertical", fontFamily:"inherit", lineHeight:1.5 }}
            />
            {approveError && <p style={{ fontSize:12, color:brand.danger, marginTop:6, marginBottom:0 }}>{approveError}</p>}
            <div style={{ display:"flex", gap:10, marginTop:16 }}>
              <button onClick={handleApprove} disabled={approving || !approveReason.trim()}
                style={{ ...S.primaryBtn, flex:1, opacity:approving||!approveReason.trim()?0.5:1, cursor:approving||!approveReason.trim()?"not-allowed":"pointer" }}>
                {approving ? "Saving…" : "Confirm Approval"}
              </button>
              <button onClick={() => { setApproveOpen(false); setApproveReason(""); setApproveError(null); }} disabled={approving}
                style={{ ...S.ghostBtn, flex:1, padding:"12px 24px" }}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Revoke modal */}
      {revokeOpen && (
        <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.7)", zIndex:200, display:"flex", alignItems:"center", justifyContent:"center" }}
          onClick={() => { if (!revoking) setRevokeOpen(false); }}>
          <div style={{ background:"#001A22", border:`1px solid ${brand.tealBorder}`, borderRadius:12, padding:28, width:380, maxWidth:"90vw" }}
            onClick={e => e.stopPropagation()}>
            <div style={{ fontSize:15, fontWeight:700, color:brand.white, marginBottom:6 }}>Revoke Approval</div>
            <p style={{ fontSize:13, color:brand.muted, marginTop:0, marginBottom:20 }}>
              Are you sure? <strong style={{ color:brand.white }}>{h.client}</strong> will revert to its original failed status.
            </p>
            {revokeError && <p style={{ fontSize:12, color:brand.danger, marginTop:0, marginBottom:12 }}>{revokeError}</p>}
            <div style={{ display:"flex", gap:10 }}>
              <button onClick={handleRevoke} disabled={revoking}
                style={{ flex:1, padding:"12px 24px", fontSize:13, fontWeight:600, borderRadius:8, cursor:revoking?"not-allowed":"pointer", border:`1px solid rgba(235,23,0,0.5)`, background:"rgba(235,23,0,0.1)", color:brand.danger, opacity:revoking?0.6:1 }}>
                {revoking ? "Revoking…" : "Revoke"}
              </button>
              <button onClick={() => setRevokeOpen(false)} disabled={revoking}
                style={{ ...S.ghostBtn, flex:1, padding:"12px 24px" }}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      <div style={{ ...S.card }}>
        <div onClick={() => setOpen(o=>!o)} style={{ padding:20, display:"flex", justifyContent:"space-between", alignItems:"flex-start", gap:16, cursor:"pointer" }}>
          <div style={{ flex:1 }}>
            <div style={{ display:"flex", alignItems:"center", gap:10, flexWrap:"wrap", marginBottom:12 }}>
              <span style={{ fontSize:15, fontWeight:700, color:brand.white }}>{h.client}</span>
              <Badge status={h.status} />
              {h.isManuallyApproved && (
                <ManuallyApprovedBadge approvedBy={h.approvedBy} approvedAt={h.approvedAt} reason={h.approvalReason} />
              )}
              <span style={{ background:"rgba(255,255,255,0.06)", borderRadius:4, padding:"2px 8px", fontSize:11, color:brand.muted, letterSpacing:"0.06em", textTransform:"uppercase" }}>{h.type}</span>
            </div>
            <div style={{ display:"flex", gap:24, flexWrap:"wrap", alignItems:"flex-start" }}>
              {h.completedAt && (
                <div>
                  <div style={{ fontSize:10, color:brand.white, letterSpacing:"0.1em", textTransform:"uppercase", fontWeight:600, marginBottom:4 }}>Last Run Client Time</div>
                  <div style={{ fontSize:13, color:brand.muted }}>{h.completedAt}</div>
                </div>
              )}
              {h.completedAtIso && (
                <div>
                  <div style={{ fontSize:10, color:brand.white, letterSpacing:"0.1em", textTransform:"uppercase", fontWeight:600, marginBottom:4 }}>Last Run Our Time</div>
                  <div style={{ fontSize:13, color:brand.muted }}>{formatLocalDateTime(h.completedAtIso)}</div>
                </div>
              )}
              {h.failureReason ? (
                <div>
                  <div style={{ fontSize:10, color:brand.white, letterSpacing:"0.1em", textTransform:"uppercase", fontWeight:600, marginBottom:4 }}>Failure Reason</div>
                  <div style={{ fontSize:13, fontWeight:700, color:brand.danger }}>{h.failureReason}</div>
                </div>
              ) : (
                <div>
                  <div style={{ fontSize:10, color:brand.white, letterSpacing:"0.1em", textTransform:"uppercase", fontWeight:600, marginBottom:4 }}>Match Score</div>
                  <div style={{ fontSize:13, fontWeight:700, color:h.status==="correct"?brand.success:brand.danger }}>{h.matchScore}%</div>
                </div>
              )}
              {h.scheduledAt && (
                <div>
                  <div style={{ fontSize:10, color:brand.white, letterSpacing:"0.1em", textTransform:"uppercase", fontWeight:600, marginBottom:4 }}>Scheduled For Client Time</div>
                  <div style={{ fontSize:13, color:brand.muted }}>{h.scheduledAt}</div>
                </div>
              )}
              {h.scheduledAtIso && (
                <div>
                  <div style={{ fontSize:10, color:brand.white, letterSpacing:"0.1em", textTransform:"uppercase", fontWeight:600, marginBottom:4 }}>Scheduled For Our Time</div>
                  <div style={{ fontSize:13, color:brand.muted }}>{formatLocalDateTime(h.scheduledAtIso)}</div>
                </div>
              )}
              {h.ianaTimezone && (
                <div>
                  <div style={{ fontSize:10, color:brand.white, letterSpacing:"0.1em", textTransform:"uppercase", fontWeight:600, marginBottom:4 }}>Location</div>
                  <div style={{ fontSize:13, color:brand.muted }}>{formatTimezone(h.ianaTimezone)}</div>
                </div>
              )}
              <div>
                <div style={{ fontSize:10, color:brand.white, letterSpacing:"0.1em", textTransform:"uppercase", fontWeight:600, marginBottom:4 }}>Number Dialled</div>
                <div style={{ display:"flex", flexWrap:"wrap", gap:4 }}>
                  {h.numbers.map((n,i) => <span key={i} style={{ background:`rgba(0,255,184,0.1)`, border:`1px solid rgba(0,255,184,0.2)`, borderRadius:4, padding:"2px 8px", fontSize:12, color:brand.turquoise }}>{n}</span>)}
                </div>
              </div>
              {h.uploadedName && (
                <div>
                  <div style={{ fontSize:10, color:brand.white, letterSpacing:"0.1em", textTransform:"uppercase", fontWeight:600, marginBottom:4 }}>Audio File</div>
                  <div style={{ fontSize:13, color:brand.muted }}>♪ {h.uploadedName}</div>
                </div>
              )}
              {h.email && (
                <div>
                  <div style={{ fontSize:10, color:brand.white, letterSpacing:"0.1em", textTransform:"uppercase", fontWeight:600, marginBottom:4 }}>Contact</div>
                  <div style={{ fontSize:13, color:brand.muted }}>✉ {h.email}</div>
                </div>
              )}
              {h.caseUrl && (
                <div>
                  <div style={{ fontSize:10, color:brand.white, letterSpacing:"0.1em", textTransform:"uppercase", fontWeight:600, marginBottom:4 }}>Case URL</div>
                  <div style={{ fontSize:13 }}><a href={h.caseUrl} target="_blank" rel="noopener noreferrer" style={{ color:brand.turquoise }}>{h.caseUrl}</a></div>
                </div>
              )}
              {h.programmedBy && (
                <div>
                  <div style={{ fontSize:10, color:brand.white, letterSpacing:"0.1em", textTransform:"uppercase", fontWeight:600, marginBottom:4 }}>Delivery By</div>
                  <div style={{ fontSize:13, color:brand.muted }}>{PROGRAMMED_BY_LABELS[h.programmedBy] ?? h.programmedBy}</div>
                </div>
              )}
            </div>
          </div>
          <div style={{ display:"flex", alignItems:"center", gap:10, flexShrink:0 }}>
            {canRerun && (
              <button onClick={e => { e.stopPropagation(); onRerun(h); }}
                style={{ ...S.ghostBtn, fontSize:12, color:brand.turquoise, borderColor:"rgba(0,255,184,0.3)" }}>
                Re-run
              </button>
            )}
            {h.isManuallyApproved && (
              <button onClick={e => { e.stopPropagation(); setRevokeError(null); setRevokeOpen(true); }}
                style={{ ...S.ghostBtn, fontSize:12, color:brand.muted, borderColor:brand.tealBorder }}>
                Revoke
              </button>
            )}
            <span style={{ color:brand.red, fontSize:14 }}>{open?"▲":"▼"}</span>
          </div>
        </div>
        {open && (
          <div style={{ borderTop:`1px solid ${brand.tealBorder}`, padding:20, background:"rgba(0,0,0,0.15)", display:"flex", flexDirection:"column", gap:16, borderRadius:"0 0 12px 12px" }}>
            <DualAudioPlayer uploadedBuffer={uploaded} uploadedName={h.uploadedName} recordingBuffer={recording} recordingDownloadUrl={h.recordingDownloadUrl} referenceTranscript={h.referenceTranscript} recordingTranscript={h.recordingTranscript} isReferenceMultilingual={h.isMultilingualReference} isRecordingMultilingual={h.isRecordingMultilingual} displayReferenceTranscript={h.displayReferenceTranscript}
              action={canApprove ? (
                <button onClick={() => setApproveOpen(true)}
                  style={{ ...S.ghostBtn, fontSize:12, color:"#ffd966", borderColor:"rgba(255,191,0,0.4)" }}>
                  Mark as correct
                </button>
              ) : null} />
            {h.failureReason ? (
              <div>
                <label style={S.label}>Failure Reason</label>
                <div style={{ fontSize:14, fontWeight:600, color:brand.danger }}>{h.failureReason}</div>
              </div>
            ) : (
              <div>
                <label style={S.label}>Match Score — {h.matchScore}%</label>
                <div style={{ background:brand.tealBorder, borderRadius:4, height:6, overflow:"hidden" }}>
                  <div style={{ width:`${h.matchScore}%`, height:6, borderRadius:4, background:h.status==="correct"?`linear-gradient(90deg,${brand.turquoise},#00c890)`:`linear-gradient(90deg,${brand.red},${brand.pink})` }} />
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </>
  );
}

// ---------------------------------------------------------------------------
// History filter rail — filter state, URL sync, rail + tab components
// ---------------------------------------------------------------------------
interface HistoryFilters {
  outcome: "all" | "correct" | "incorrect" | "failed";
  manual: boolean;
  scheduled: boolean;
  from: string;
  to: string;
  datePreset: "" | "24h" | "7d" | "30d" | "custom";
  scoreBelow: number;
  approval: "all" | "approved" | "not-approved";
  programmedBy: "all" | "phmg" | "client" | "maintainer";
  search: string;
  sort: "newest" | "oldest" | "score_asc";
}

const defaultHistoryFilters: HistoryFilters = {
  outcome: "all", manual: true, scheduled: true,
  from: "", to: "", datePreset: "", scoreBelow: 100,
  approval: "all", programmedBy: "all", search: "", sort: "newest",
};

type HistoryFiltersAction =
  | { type: "SET_OUTCOME"; outcome: HistoryFilters["outcome"] }
  | { type: "TOGGLE_TRIGGER"; trigger: "manual" | "scheduled" }
  | { type: "SET_DATE_PRESET"; preset: "24h" | "7d" | "30d" }
  | { type: "SET_DATE_FIELD"; field: "from" | "to"; value: string }
  | { type: "SET_SCORE_BELOW"; value: number }
  | { type: "SET_APPROVAL"; approval: HistoryFilters["approval"] }
  | { type: "SET_PROGRAMMED_BY"; programmedBy: HistoryFilters["programmedBy"] }
  | { type: "SET_SEARCH"; value: string }
  | { type: "SET_SORT"; value: HistoryFilters["sort"] }
  | { type: "RESET" };

function historyFiltersReducer(state: HistoryFilters, action: HistoryFiltersAction): HistoryFilters {
  switch (action.type) {
    case "SET_OUTCOME":
      return { ...state, outcome: action.outcome };
    case "TOGGLE_TRIGGER": {
      const next = { ...state, [action.trigger]: !state[action.trigger] };
      // Neither checked is disallowed — flip both back on rather than showing an empty list.
      if (!next.manual && !next.scheduled) return { ...state, manual: true, scheduled: true };
      return next;
    }
    case "SET_DATE_PRESET": {
      const today = todayLocalDateStr();
      const days = action.preset === "24h" ? 0 : action.preset === "7d" ? 6 : 29;
      return { ...state, datePreset: action.preset, from: addDaysToLocalDateStr(today, -days), to: today };
    }
    case "SET_DATE_FIELD": {
      let { from, to } = state;
      if (action.field === "from") {
        from = action.value;
        if (to && from > to) to = from;
      } else {
        to = action.value;
        if (from && to < from) from = to;
      }
      return { ...state, from, to, datePreset: "custom" };
    }
    case "SET_SCORE_BELOW":
      return { ...state, scoreBelow: action.value };
    case "SET_APPROVAL":
      return { ...state, approval: action.approval };
    case "SET_PROGRAMMED_BY":
      return { ...state, programmedBy: action.programmedBy };
    case "SET_SEARCH":
      return { ...state, search: action.value };
    case "SET_SORT":
      return { ...state, sort: action.value };
    case "RESET":
      return { ...defaultHistoryFilters };
    default:
      return state;
  }
}

function parseHistoryFiltersFromUrl(): { filters: HistoryFilters; page: number } {
  const params = new URLSearchParams(window.location.search);
  const outcomeParam = params.get("outcome");
  const approvalParam = params.get("approval");
  const programmedByParam = params.get("programmedBy");
  const trigger = params.get("trigger");
  const from = params.get("from") || "";
  const to = params.get("to") || "";
  const filters: HistoryFilters = {
    ...defaultHistoryFilters,
    outcome: outcomeParam === "correct" || outcomeParam === "incorrect" || outcomeParam === "failed" ? outcomeParam : "all",
    manual: trigger ? trigger.split(",").includes("manual") : true,
    scheduled: trigger ? trigger.split(",").includes("scheduled") : true,
    from, to,
    datePreset: from || to ? "custom" : "",
    scoreBelow: params.get("scoreBelow") ? Number(params.get("scoreBelow")) : 100,
    approval: approvalParam === "approved" || approvalParam === "not-approved" ? approvalParam : "all",
    programmedBy: ["phmg", "client", "maintainer"].includes(programmedByParam) ? programmedByParam as HistoryFilters["programmedBy"] : "all",
    search: params.get("q") || "",
    sort: (params.get("sort") as HistoryFilters["sort"]) || "newest",
  };
  const page = params.get("page") ? Number(params.get("page")) : 1;
  return { filters, page: Number.isFinite(page) && page > 0 ? page : 1 };
}

function buildHistoryUrlParams(filters: HistoryFilters, page: number): URLSearchParams {
  const params = new URLSearchParams();
  if (filters.outcome !== "all") params.set("outcome", filters.outcome);
  if (filters.manual !== filters.scheduled) params.set("trigger", filters.manual ? "manual" : "scheduled");
  if (filters.from) params.set("from", filters.from);
  if (filters.to) params.set("to", filters.to);
  if (filters.scoreBelow < 100) params.set("scoreBelow", String(filters.scoreBelow));
  if (filters.approval !== "all") params.set("approval", filters.approval);
  if (filters.programmedBy !== "all") params.set("programmedBy", filters.programmedBy);
  if (filters.search) params.set("q", filters.search);
  if (filters.sort !== "newest") params.set("sort", filters.sort);
  if (page > 1) params.set("page", String(page));
  return params;
}

function activeHistoryFilterCount(f: HistoryFilters): number {
  let n = 0;
  if (f.outcome !== "all") n++;
  if (f.manual !== f.scheduled) n++;
  if (f.from || f.to) n++;
  if (f.scoreBelow < 100) n++;
  if (f.approval !== "all") n++;
  if (f.programmedBy !== "all") n++;
  return n;
}

type FilterSectionKey = "outcome" | "trigger" | "date" | "score" | "approval" | "programmedBy";

// Collapsible wrapper for one facet in the rail. `active` drives the dot shown when
// the section is closed — otherwise a narrowed filter would be invisible once folded
// away. Open sections need no dot; their own controls already show the selection.
function FilterSection({ title, open, active, onToggle, children }: {
  title: string;
  open: boolean;
  active: boolean;
  onToggle: () => void;
  children: React.ReactNode;
}) {
  return (
    <div style={{ marginBottom: open ? 20 : 4 }}>
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        style={{
          ...S.label,
          display: "flex", alignItems: "center", gap: 6, width: "100%",
          background: "none", border: "none", padding: "6px 0", marginBottom: open ? 8 : 0,
          cursor: "pointer", textAlign: "left", font: "inherit",
          fontSize: 12, letterSpacing: "0.08em", textTransform: "uppercase", fontWeight: 600,
          color: open ? brand.muted : brand.mutedDark,
        }}
      >
        <svg
          width="8" height="8" viewBox="0 0 8 8" aria-hidden="true"
          style={{ flexShrink: 0, transform: open ? "rotate(90deg)" : "none", transition: "transform 0.15s" }}
        >
          <path d="M1 0 L7 4 L1 8 Z" fill="currentColor" />
        </svg>
        <span style={{ flex: 1 }}>{title}</span>
        {!open && active && (
          <span
            aria-label="filter active"
            style={{ width: 6, height: 6, borderRadius: "50%", background: brand.turquoise, flexShrink: 0 }}
          />
        )}
      </button>
      {open && children}
    </div>
  );
}

function HistoryFilterRail({ filters, dispatch, allCount, correctCount, incorrectCount, failedCount, totalShowing, totalMatching, variant = "rail", onClose }: {
  filters: HistoryFilters;
  dispatch: (action: HistoryFiltersAction) => void;
  allCount: number;
  correctCount: number;
  incorrectCount: number;
  failedCount: number;
  totalShowing: number;
  totalMatching: number;
  variant?: "rail" | "drawer";
  onClose?: () => void;
}) {
  const [openSections, setOpenSections] = useState<Record<FilterSectionKey, boolean>>({
    outcome: true, trigger: true, date: true, score: true, approval: true, programmedBy: true,
  });
  const toggleSection = (key: FilterSectionKey) =>
    setOpenSections(s => ({ ...s, [key]: !s[key] }));
  const outcomeRow = (value: HistoryFilters["outcome"], label: string, count: number, countColor: string) => {
    const active = filters.outcome === value;
    return (
      <div
        key={value}
        role="radio"
        aria-checked={active}
        tabIndex={0}
        onClick={() => dispatch({ type: "SET_OUTCOME", outcome: value })}
        onKeyDown={e => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); dispatch({ type: "SET_OUTCOME", outcome: value }); } }}
        style={{
          ...S.ghostBtn,
          display: "flex", justifyContent: "space-between", alignItems: "center",
          width: "100%", height: 34, boxSizing: "border-box", marginBottom: 6, cursor: "pointer",
          background: active ? `linear-gradient(135deg, ${brand.red}, ${brand.pink})` : "transparent",
          color: active ? brand.white : brand.muted,
          borderColor: active ? "transparent" : brand.tealBorder,
          fontWeight: active ? 700 : 500,
        }}
      >
        <span>{label}</span>
        <span style={{ color: active ? brand.white : countColor }}>{count}</span>
      </div>
    );
  };

  // Same row treatment as the outcome radios, minus the count — the API returns per-outcome
  // totals only, so there is no approved/not-approved tally to show here.
  const approvalRow = (value: HistoryFilters["approval"], label: string) => {
    const active = filters.approval === value;
    return (
      <div
        key={value}
        role="radio"
        aria-checked={active}
        tabIndex={0}
        onClick={() => dispatch({ type: "SET_APPROVAL", approval: value })}
        onKeyDown={e => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); dispatch({ type: "SET_APPROVAL", approval: value }); } }}
        style={{
          ...S.ghostBtn,
          display: "flex", alignItems: "center",
          width: "100%", height: 34, boxSizing: "border-box", marginBottom: 6, cursor: "pointer",
          background: active ? `linear-gradient(135deg, ${brand.red}, ${brand.pink})` : "transparent",
          color: active ? brand.white : brand.muted,
          borderColor: active ? "transparent" : brand.tealBorder,
          fontWeight: active ? 700 : 500,
        }}
      >
        <span>{label}</span>
      </div>
    );
  };

  const programmedByRow = (value: HistoryFilters["programmedBy"], label: string) => {
    const active = filters.programmedBy === value;
    return (
      <div
        key={value}
        role="radio"
        aria-checked={active}
        tabIndex={0}
        onClick={() => dispatch({ type: "SET_PROGRAMMED_BY", programmedBy: value })}
        onKeyDown={e => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); dispatch({ type: "SET_PROGRAMMED_BY", programmedBy: value }); } }}
        style={{
          ...S.ghostBtn,
          display: "flex", alignItems: "center",
          width: "100%", height: 34, boxSizing: "border-box", marginBottom: 6, cursor: "pointer",
          background: active ? `linear-gradient(135deg, ${brand.red}, ${brand.pink})` : "transparent",
          color: active ? brand.white : brand.muted,
          borderColor: active ? "transparent" : brand.tealBorder,
          fontWeight: active ? 700 : 500,
        }}
      >
        <span>{label}</span>
      </div>
    );
  };

  const presetBtn = (preset: "24h" | "7d" | "30d", label: string) => {
    const active = filters.datePreset === preset;
    return (
      <button
        key={preset}
        onClick={() => dispatch({ type: "SET_DATE_PRESET", preset })}
        style={{
          ...S.ghostBtn, fontSize: 11, padding: "6px 10px",
          background: active ? `linear-gradient(135deg, ${brand.red}, ${brand.pink})` : "transparent",
          color: active ? brand.white : brand.muted,
          borderColor: active ? "transparent" : brand.tealBorder,
          fontWeight: active ? 700 : 500,
        }}
      >
        {label}
      </button>
    );
  };

  const invalidRange = !!(filters.from && filters.to && filters.to < filters.from);

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%", padding: "20px 16px", boxSizing: "border-box" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
        <div style={{ fontSize: 11, color: brand.turquoise, letterSpacing: "0.12em", textTransform: "uppercase", fontWeight: 700 }}>Filters</div>
        <button onClick={() => dispatch({ type: "RESET" })}
          style={{ background: "none", border: "none", color: brand.muted, fontSize: 12, cursor: "pointer", textDecoration: "underline", padding: 0 }}>
          Reset
        </button>
      </div>

      <div className="sc-scroll" style={{ flex: 1, overflowY: "auto", overflowX: "hidden" }}>
        <FilterSection
          title="Outcome"
          open={openSections.outcome}
          active={filters.outcome !== "all"}
          onToggle={() => toggleSection("outcome")}
        >
          <div role="radiogroup" aria-label="Outcome">
            {outcomeRow("all", "All outcomes", allCount, brand.muted)}
            {outcomeRow("correct", "Correct", correctCount, brand.turquoise)}
            {outcomeRow("incorrect", "Incorrect", incorrectCount, brand.red)}
            {outcomeRow("failed", "Failed", failedCount, brand.red)}
          </div>
        </FilterSection>

        <FilterSection
          title="Trigger"
          open={openSections.trigger}
          active={filters.manual !== filters.scheduled}
          onToggle={() => toggleSection("trigger")}
        >
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, color: brand.white, cursor: "pointer" }}>
              <input type="checkbox" checked={filters.manual} onChange={() => dispatch({ type: "TOGGLE_TRIGGER", trigger: "manual" })} />
              Manual
            </label>
            <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, color: brand.white, cursor: "pointer" }}>
              <input type="checkbox" checked={filters.scheduled} onChange={() => dispatch({ type: "TOGGLE_TRIGGER", trigger: "scheduled" })} />
              Scheduled
            </label>
          </div>
        </FilterSection>

        <FilterSection
          title="Date run"
          open={openSections.date}
          active={!!(filters.from || filters.to)}
          onToggle={() => toggleSection("date")}
        >
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 10 }}>
            {presetBtn("24h", "24h")}
            {presetBtn("7d", "7 days")}
            {presetBtn("30d", "30 days")}
            <div
              style={{
                ...S.ghostBtn, fontSize: 11, padding: "6px 10px", cursor: "default",
                background: filters.datePreset === "custom" ? `linear-gradient(135deg, ${brand.red}, ${brand.pink})` : "transparent",
                color: filters.datePreset === "custom" ? brand.white : brand.muted,
                borderColor: filters.datePreset === "custom" ? "transparent" : brand.tealBorder,
                fontWeight: filters.datePreset === "custom" ? 700 : 500,
              }}
            >
              Custom
            </div>
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            <div>
              <div style={{ fontSize: 10, color: brand.muted, marginBottom: 4, letterSpacing: "0.06em", textTransform: "uppercase" }}>From</div>
              <input
                type="date"
                aria-label="From date"
                value={filters.from}
                onChange={e => dispatch({ type: "SET_DATE_FIELD", field: "from", value: e.target.value })}
                style={{ ...S.inputBase, colorScheme: "dark", padding: "6px 8px", fontSize: 12, width: "100%", boxSizing: "border-box", borderColor: invalidRange ? brand.danger : brand.tealBorder }}
              />
            </div>
            <div>
              <div style={{ fontSize: 10, color: brand.muted, marginBottom: 4, letterSpacing: "0.06em", textTransform: "uppercase" }}>To</div>
              <input
                type="date"
                aria-label="To date"
                value={filters.to}
                onChange={e => dispatch({ type: "SET_DATE_FIELD", field: "to", value: e.target.value })}
                style={{ ...S.inputBase, colorScheme: "dark", padding: "6px 8px", fontSize: 12, width: "100%", boxSizing: "border-box", borderColor: invalidRange ? brand.danger : brand.tealBorder }}
              />
            </div>
          </div>
        </FilterSection>

        <FilterSection
          title="Match score below"
          open={openSections.score}
          active={filters.scoreBelow < 100}
          onToggle={() => toggleSection("score")}
        >
          <input
            type="range"
            min={0} max={100} step={5}
            value={filters.scoreBelow}
            onChange={e => dispatch({ type: "SET_SCORE_BELOW", value: Number(e.target.value) })}
            aria-valuetext={filters.scoreBelow >= 100 ? "no minimum" : `below ${filters.scoreBelow}%`}
            style={{ width: "100%", accentColor: brand.red }}
          />
          <div style={{ fontSize: 12, color: brand.muted, textAlign: "right" }}>
            {filters.scoreBelow >= 100 ? "Off" : `Below ${filters.scoreBelow}%`}
          </div>
        </FilterSection>

        <FilterSection
          title="Manual approval"
          open={openSections.approval}
          active={filters.approval !== "all"}
          onToggle={() => toggleSection("approval")}
        >
          <div role="radiogroup" aria-label="Manual approval">
            {approvalRow("all", "All checks")}
            {approvalRow("approved", "Approved only")}
            {approvalRow("not-approved", "Exclude approved")}
          </div>
        </FilterSection>

        <FilterSection
          title="Delivery By"
          open={openSections.programmedBy}
          active={filters.programmedBy !== "all"}
          onToggle={() => toggleSection("programmedBy")}
        >
          <div role="radiogroup" aria-label="Delivery By">
            {programmedByRow("all", "All checks")}
            {programmedByRow("phmg", "PHMG")}
            {programmedByRow("client", "Client")}
            {programmedByRow("maintainer", "Maintainer")}
          </div>
        </FilterSection>
      </div>

      <div style={{ borderTop: `1px solid ${brand.tealBorder}`, paddingTop: 12, fontSize: 12, color: brand.muted }}>
        Showing {totalShowing} of {totalMatching}
      </div>

      {variant === "drawer" && onClose && (
        <button onClick={onClose} style={{ ...S.ghostBtn, marginTop: 12 }}>Close</button>
      )}
    </div>
  );
}

function HistoryTab({
  history, loading, filters, dispatch,
  allCount, correctCount, incorrectCount, failedCount,
  page, total, pageSize, onPage,
  onRefresh, onRerun, onApprovalChange,
  pinnedRecord, pinnedLoading, onClearPinned, onRefreshPinned,
}: {
  history: any[]; loading: boolean; filters: HistoryFilters; dispatch: (action: HistoryFiltersAction) => void;
  allCount: number; correctCount: number; incorrectCount: number; failedCount: number;
  page: number; total: number; pageSize: number; onPage: (p: number) => void;
  onRefresh: () => void; onRerun: (h: any) => void; onApprovalChange: () => void;
  pinnedRecord?: any; pinnedLoading?: boolean; onClearPinned?: () => void; onRefreshPinned?: () => void;
}) {
  const isNarrow = useMediaQuery("(max-width: 1100px)");
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [searchInput, setSearchInput] = useState(filters.search);
  const drawerRef = useRef<HTMLDivElement>(null);

  useEffect(() => { setSearchInput(filters.search); }, [filters.search]);
  useEffect(() => {
    const t = setTimeout(() => { if (searchInput !== filters.search) dispatch({ type: "SET_SEARCH", value: searchInput }); }, 250);
    return () => clearTimeout(t);
  }, [searchInput]);

  useEffect(() => {
    if (!drawerOpen) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") { setDrawerOpen(false); return; }
      if (e.key !== "Tab" || !drawerRef.current) return;
      const focusables = drawerRef.current.querySelectorAll<HTMLElement>('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])');
      if (focusables.length === 0) return;
      const first = focusables[0], last = focusables[focusables.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [drawerOpen]);

  const rail = (
    <HistoryFilterRail
      filters={filters} dispatch={dispatch}
      allCount={allCount} correctCount={correctCount} incorrectCount={incorrectCount} failedCount={failedCount}
      totalShowing={history.length} totalMatching={total}
      variant={isNarrow ? "drawer" : "rail"}
      onClose={isNarrow ? () => setDrawerOpen(false) : undefined}
    />
  );

  const activeCount = activeHistoryFilterCount(filters);

  if (pinnedLoading) {
    return (
      <div style={{ textAlign:"center", padding:"60px 0" }}>
        <style>{`@keyframes sc-spin { to { transform: rotate(360deg); } }`}</style>
        <div style={{ width:36, height:36, border:`3px solid ${brand.tealBorder}`, borderTopColor:brand.turquoise, borderRadius:"50%", animation:"sc-spin 0.7s linear infinite", margin:"0 auto" }} />
      </div>
    );
  }

  if (pinnedRecord) {
    return (
      <div>
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:16, flexWrap:"wrap", gap:12, background:brand.tealLight, border:`1px solid ${brand.tealBorder}`, borderRadius:8, padding:"10px 16px" }}>
          <span style={{ fontSize:13, color:brand.muted }}>Showing the result linked from your email.</span>
          <button onClick={onClearPinned} style={S.ghostBtn}>View full history</button>
        </div>
        <HistoryItem h={pinnedRecord} onRerun={onRerun} onRefresh={onRefreshPinned} />
      </div>
    );
  }

  const content = (
    <div style={{ position: "relative" }}>
      <style>{`@keyframes sc-spin { to { transform: rotate(360deg); } }`}</style>
      {loading && (
        <div style={{ position:"absolute", inset:0, background:"rgba(0,36,46,0.7)", borderRadius:8, display:"flex", alignItems:"flex-start", justifyContent:"center", paddingTop:80, zIndex:10 }}>
          <div style={{ width:36, height:36, border:`3px solid ${brand.tealBorder}`, borderTopColor:brand.turquoise, borderRadius:"50%", animation:"sc-spin 0.7s linear infinite" }} />
        </div>
      )}
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:8, flexWrap:"wrap", gap:12 }}>
        <div style={S.sectionHd}>Sound Check History</div>
        <div style={{ display:"flex", gap:6, flexWrap:"wrap", alignItems:"center" }}>
          {isNarrow && (
            <button onClick={() => setDrawerOpen(true)} style={{ ...S.ghostBtn, display: "flex", alignItems: "center", gap: 6 }}>
              Filters
              {activeCount > 0 && (
                <span style={{ background: brand.red, color: brand.white, borderRadius: 10, padding: "1px 6px", fontSize: 10, fontWeight: 700 }}>
                  {activeCount}
                </span>
              )}
            </button>
          )}
          <input
            type="text"
            placeholder="Search client, contact, number or audio file…"
            value={searchInput}
            onChange={e => setSearchInput(e.target.value)}
            style={{ background:brand.tealLight, border:`1px solid ${brand.tealBorder}`, borderRadius:7, padding:"6px 12px", color:brand.white, fontSize:12, outline:"none", width:220 }}
          />
          <select value={filters.sort} onChange={e => dispatch({ type: "SET_SORT", value: e.target.value as HistoryFilters["sort"] })}
            style={{ background:brand.tealLight, border:`1px solid ${brand.tealBorder}`, borderRadius:7, padding:"6px 10px", color:brand.white, fontSize:12, outline:"none", cursor:"pointer" }}>
            <option value="newest">Newest first</option>
            <option value="oldest">Oldest first</option>
            <option value="score_asc">Lowest score first</option>
          </select>
          <RefreshButton onRefresh={onRefresh} />
        </div>
      </div>
      <RedLine />
      {history.length===0 ? (
        <div style={{ textAlign:"center", padding:"40px 0" }}>
          <p style={{ color:brand.mutedDark, marginBottom:12 }}>No results found.</p>
          <button onClick={() => dispatch({ type: "RESET" })} style={S.ghostBtn}>Clear filters</button>
        </div>
      ) : (
        <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
          {history.map(h => <HistoryItem key={h.id} h={h} onRerun={onRerun} onRefresh={onApprovalChange} />)}
        </div>
      )}
      <Pagination page={page} total={total} pageSize={pageSize} onPage={onPage} />
    </div>
  );

  if (isNarrow) {
    return (
      <div>
        {content}
        {drawerOpen && (
          <div style={{ position:"fixed", inset:0, zIndex:100, display:"flex" }}>
            <div onClick={() => setDrawerOpen(false)} style={{ position:"absolute", inset:0, background:"rgba(0,0,0,0.6)" }} />
            <div ref={drawerRef} className="sc-scroll" style={{ position:"relative", width:280, height:"100%", background:brand.tealLight, borderRight:`1px solid ${brand.tealBorder}`, overflowY:"auto", overflowX:"hidden" }}>
              {rail}
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div style={{ display:"grid", gridTemplateColumns:"268px 1fr", gap:0 }}>
      <div className="sc-scroll" style={{ borderRight:`1px solid ${brand.tealBorder}`, position:"sticky", top:64, alignSelf:"start", height:"calc(100vh - 64px)", overflowY:"auto", overflowX:"hidden" }}>
        {rail}
      </div>
      <div style={{ paddingLeft:20 }}>
        {content}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// App
// ---------------------------------------------------------------------------
const PROGRAMMED_BY_LABELS = { Phmg: "PHMG", Client: "CLIENT", Maintainer: "MAINTAINER" };

function mapDto(dto) {
  const callResults = dto.callResults || [];
  const firstCompleted = callResults.find(r => r.status === "Completed");

  let scheduledAtDisplay = "";
  if (dto.localScheduledAt && dto.ianaTimezone) {
    const m = dto.localScheduledAt.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/);
    if (m) {
      scheduledAtDisplay = `${m[3]}/${m[2]}/${m[1]} ${m[4]}:${m[5]}`;
    }
  } else if (dto.scheduledAt) {
    scheduledAtDisplay = new Date(dto.scheduledAt).toLocaleString("en-GB").replace(",","");
  }

  const status = dto.status === "Completed"
                   ? (dto.isManuallyApproved || dto.matchScore >= 100 ? "correct" : "incorrect")
                   : dto.status === "TranscriptionFailed" ? "transcription-error"
                   : dto.status?.toLowerCase() ?? "pending";

  return {
    id:                  dto.id,
    client:              dto.client,
    email:               dto.requesterEmail,
    numbers:             callResults.map(r => r.phoneNumber),
    status,
    // Only surface a per-call failure reason when the check didn't actually pass overall — a
    // multi-number check can have one leg fail (no answer) while another scores highly enough
    // to still pass the check as a whole, and showing a failure reason there would hide a
    // legitimate pass behind what looks like an error.
    failureReason:       status !== "correct"
                           ? (callResults.find(r => r.failureReason)?.failureReason ?? null)
                           : null,
    isManuallyApproved:  dto.isManuallyApproved ?? false,
    approvalReason:      dto.approvalReason ?? null,
    approvedBy:          dto.approvedBy ?? null,
    approvedAt:          dto.approvedAt ? new Date(dto.approvedAt).toLocaleString("en-GB").replace(",","") : null,
    matchScore:          dto.matchScore ?? 0,
    referenceTranscript:       firstCompleted?.referenceTranscript ?? null,
    recordingTranscript:       firstCompleted?.recordingTranscript ?? null,
    displayReferenceTranscript: firstCompleted?.displayReferenceTranscript ?? null,
    recordingDownloadUrl:      firstCompleted?.recordingDownloadUrl ?? null,
    isMultilingualReference:   dto.isMultilingualReference ?? false,
    isRecordingMultilingual:   firstCompleted?.isRecordingMultilingual ?? false,
    uploadedFileUrl:           dto.uploadedFileUrl ?? null,
    uploadedName:              dto.uploadedFileName || (dto.uploadedFileUrl ? "Reference Audio" : null),
    completedAt:         dto.completedAt
                           ? (dto.ianaTimezone ? formatInTimezone(dto.completedAt, dto.ianaTimezone) : new Date(dto.completedAt).toLocaleString("en-GB").replace(",",""))
                           : "",
    completedAtIso:      dto.completedAt ?? null,
    scheduledAt:         scheduledAtDisplay,
    scheduledAtIso:      dto.scheduledAt ?? null,
    localScheduledAt:    dto.localScheduledAt ?? null,
    ianaTimezone:        dto.ianaTimezone ?? null,
    type:                dto.scheduledAt ? "scheduled" : "manual",
    caseUrl:             dto.caseUrl ?? null,
    programmedBy:        dto.programmedBy ?? null,
    callResults,
  };
}

const PAGE_SIZE = 20;

function AuthenticatedApp() {
  const initialHistoryId = () => new URLSearchParams(window.location.search).get("historyId");
  const [tab, setTab] = useState(() => initialHistoryId() ? "History" : "Dashboard");

  const [pinnedRecord, setPinnedRecord]   = useState(null);
  const [pinnedLoading, setPinnedLoading] = useState(() => !!initialHistoryId());

  const loadPinnedRecord = (id) =>
    apiGetStatus(id).then(mapDto).then(setPinnedRecord).catch(() => setPinnedRecord(null));

  useEffect(() => {
    const id = initialHistoryId();
    if (!id) return;
    loadPinnedRecord(id).finally(() => setPinnedLoading(false));
  }, []);

  // Re-fetches just the pinned record (e.g. after approving/revoking it) — refreshHistory()
  // only updates the paginated `history` list, which isn't what the pinned view renders.
  const refreshPinnedRecord = () => {
    const id = initialHistoryId();
    if (id) loadPinnedRecord(id);
  };

  const clearPinnedRecord = () => {
    setPinnedRecord(null);
    const params = new URLSearchParams(window.location.search);
    params.delete("historyId");
    const qs = params.toString();
    window.history.replaceState(null, "", qs ? `${window.location.pathname}?${qs}` : window.location.pathname);
  };

  const [history, setHistory]                 = useState([]);
  const [historyTotal, setHistoryTotal]       = useState(0);
  const [historyAllCount, setHistoryAllCount] = useState(0);
  const [correctTotal, setCorrectTotal]       = useState(0);
  const [incorrectTotal, setIncorrectTotal]   = useState(0);
  const [failedTotal, setFailedTotal]         = useState(0);
  const [historyPage, setHistoryPage]         = useState(() => parseHistoryFiltersFromUrl().page);
  const [historyFilters, dispatchHistoryFilters] = useReducer(historyFiltersReducer, undefined, () => parseHistoryFiltersFromUrl().filters);
  const [historyLoading, setHistoryLoading]   = useState(false);

  const [newCheckPrefill, setNewCheckPrefill] = useState(null);

  const [scheduled, setScheduled]             = useState([]);
  const [scheduledTotal, setScheduledTotal]   = useState(0);
  const [scheduledPage, setScheduledPage]     = useState(1);
  const [scheduledSearch, setScheduledSearch] = useState("");
  const [scheduledFilters, dispatchScheduledFilters] = useReducer(scheduledFiltersReducer, defaultScheduledFilters);
  const [scheduledLoading, setScheduledLoading] = useState(false);

  const refreshHistory = (page = historyPage, filters = historyFilters) => {
    setHistoryLoading(true);
    const params = new URLSearchParams({ page: String(page), pageSize: String(PAGE_SIZE) });
    if (filters.outcome !== "all") params.set("outcome", filters.outcome);
    if (filters.manual !== filters.scheduled) params.set("trigger", filters.manual ? "manual" : "scheduled");
    if (filters.from) params.set("from", localDateStartToUtcIso(filters.from));
    if (filters.to) params.set("to", localDateExclusiveEndToUtcIso(filters.to));
    if (filters.scoreBelow < 100) params.set("scoreBelow", String(filters.scoreBelow));
    if (filters.approval !== "all") params.set("approval", filters.approval);
    if (filters.programmedBy !== "all") params.set("programmedBy", filters.programmedBy);
    if (filters.search) params.set("search", filters.search);
    if (filters.sort && filters.sort !== "newest") params.set("sortBy", filters.sort);
    authFetch(`${API_BASE}/history?${params}`)
      .then(r => (r.ok && r.status !== 204) ? r.json() : null)
      .then(data => {
        setHistoryLoading(false);
        if (!data) return;
        const total   = data.totalCount ?? 0;
        const maxPage = Math.max(1, Math.ceil(total / PAGE_SIZE));
        if (page > maxPage) { setHistoryPage(maxPage); refreshHistory(maxPage, filters); return; }
        setHistory((data.items || []).map(mapDto));
        setHistoryTotal(total);
        setHistoryAllCount(data.allCount ?? 0);
        setCorrectTotal(data.correctCount ?? 0);
        setIncorrectTotal(data.incorrectCount ?? 0);
        setFailedTotal(data.failedCount ?? 0);
      })
      .catch(() => { setHistoryLoading(false); });
  };

  const refreshScheduled = (page = scheduledPage, search = scheduledSearch, filters = scheduledFilters) => {
    setScheduledLoading(true);
    const params = new URLSearchParams({ page: String(page), pageSize: String(PAGE_SIZE) });
    if (search) params.set("search", search);
    if (filters.dateFrom) params.set("dateFrom", filters.dateFrom);
    if (filters.dateTo) params.set("dateTo", filters.dateTo);
    if (filters.programmedBy !== "all") params.set("programmedBy", filters.programmedBy);
    authFetch(`${API_BASE}/scheduled?${params}`)
      .then(r => (r.ok && r.status !== 204) ? r.json() : null)
      .then(data => {
        setScheduledLoading(false);
        if (!data) return;
        const total   = data.totalCount ?? 0;
        const maxPage = Math.max(1, Math.ceil(total / PAGE_SIZE));
        if (page > maxPage) { setScheduledPage(maxPage); refreshScheduled(maxPage, search, filters); return; }
        setScheduled((data.items || []).map(mapDto));
        setScheduledTotal(total);
      })
      .catch(() => { setScheduledLoading(false); });
  };

  // Sole fetch trigger for Scheduled data — fires on mount and whenever filters,
  // search or page change (mirrors the History fetch-trigger effect below).
  useEffect(() => {
    refreshScheduled(scheduledPage, scheduledSearch, scheduledFilters);
  }, [scheduledFilters, scheduledPage, scheduledSearch]);

  // Sole fetch trigger for History data — fires on mount (covering the initial
  // load and any URL-hydrated filters/page) and whenever filters or page change.
  useEffect(() => {
    refreshHistory(historyPage, historyFilters);
  }, [historyFilters, historyPage]);

  // Keeps the browser URL a shareable snapshot of the current History filters/page.
  useEffect(() => {
    const qs = buildHistoryUrlParams(historyFilters, historyPage).toString();
    const url = qs ? `${window.location.pathname}?${qs}` : window.location.pathname;
    window.history.replaceState(null, "", url);
  }, [historyFilters, historyPage]);

  const handleTabChange = (t: string) => {
    setTab(t);
    if (t === "Dashboard") {
      dispatchHistoryFilters({ type: "RESET" });
      setHistoryPage(1);
      refreshHistory(1, defaultHistoryFilters);
      // No explicit refreshScheduled() call needed here — dispatching RESET changes
      // scheduledFilters (a new object every time), which the "sole fetch trigger"
      // effect below picks up and fetches from, same as the filter rail's own resets do.
      dispatchScheduledFilters({ type: "RESET" });
      setScheduledPage(1);
      setScheduledSearch("");
    } else if (t === "History")   refreshHistory(historyPage, historyFilters);
    else if (t === "Scheduled")   refreshScheduled(scheduledPage, scheduledSearch, scheduledFilters);
  };

  useEffect(() => {
    if (tab === "New Check") return;
    const id = setInterval(() => {
      if (tab === "Dashboard" || tab === "History")   refreshHistory(historyPage, historyFilters);
      if (tab === "Dashboard" || tab === "Scheduled") refreshScheduled(scheduledPage, scheduledSearch, scheduledFilters);
    }, 30_000);
    return () => clearInterval(id);
  }, [tab, historyPage, historyFilters, scheduledPage, scheduledSearch, scheduledFilters]);

  const handleRerun = (h) => {
    let schedTime = "";
    if (h.localScheduledAt) {
      const m = h.localScheduledAt.match(/T(\d{2}):(\d{2})/);
      if (m) schedTime = `${m[1]}:${m[2]}`;
    }
    setNewCheckPrefill({ client: h.client, email: h.email, numbers: h.numbers, uploadedFileUrl: h.uploadedFileUrl, uploadedName: h.uploadedName, isMultilingual: h.isMultilingualReference, schedTimezone: h.ianaTimezone || "", schedTime });
    setTab("New Check");
  };

  const handleLogout = () => { CognitoService.signOut(); window.location.reload(); };

  return (
    <div style={S.page}>
      <div style={S.header}>
        <div style={{ height:3, background:`linear-gradient(90deg, ${brand.red}, ${brand.pink}, transparent)` }} />
        <div style={{ maxWidth:1100, margin:"0 auto", padding:"0 24px", height:60, display:"flex", alignItems:"center", justifyContent:"space-between" }}>
          <div style={{ display:"flex", alignItems:"center", gap:12 }}>
            {/* PHMG play-button logo mark */}
            <svg width="28" height="34" viewBox="0 0 28 34" fill="none">
              <defs>
                <linearGradient id="logoGrad" x1="0%" y1="0%" x2="0%" y2="100%">
                  <stop offset="0%" stopColor={brand.pink} />
                  <stop offset="100%" stopColor={brand.red} />
                </linearGradient>
              </defs>
              <path d="M0 0 L28 17 L0 34 Z" fill="url(#logoGrad)" />
            </svg>
            <div>
              <div style={{ fontSize:14, fontWeight:800, color:brand.white, letterSpacing:"0.06em", textTransform:"uppercase", lineHeight:1.2 }}>Sound Check</div>
              <div style={{ fontSize:10, color:brand.turquoise, letterSpacing:"0.1em", textTransform:"uppercase" }}>PHMG · CTS Department</div>
            </div>
          </div>
          <div style={{ display:"flex", alignItems:"center", gap:8 }}>
            <nav style={{ display:"flex", gap:4 }}>
              {TABS.map(t => (
                <button key={t} onClick={()=>handleTabChange(t)}
                  style={{ padding:"7px 18px", borderRadius:7, border:"none", cursor:"pointer", fontSize:12, fontWeight:600, letterSpacing:"0.07em", textTransform:"uppercase", transition:"all 0.15s", background:tab===t?`linear-gradient(135deg,${brand.red},${brand.pink})`:"transparent", color:tab===t?brand.white:brand.muted }}>
                  {t}
                </button>
              ))}
            </nav>
            {CognitoService.isEnabled() && (
              <button onClick={handleLogout}
                style={{ padding:"7px 14px", borderRadius:7, border:`1px solid rgba(122,172,181,0.3)`, cursor:"pointer", fontSize:12, fontWeight:600, letterSpacing:"0.07em", textTransform:"uppercase", background:"transparent", color:brand.muted, marginLeft:8 }}>
                Sign out
              </button>
            )}
          </div>
        </div>
      </div>
      <div style={{ maxWidth:1100, margin:"0 auto", padding:"32px 24px" }}>
        {tab==="Dashboard" && <Dashboard history={history} scheduled={scheduled} correctTotal={correctTotal} incorrectTotal={incorrectTotal} failedTotal={failedTotal} scheduledTotal={scheduledTotal} onTabChange={handleTabChange} onRefresh={() => { refreshHistory(1, defaultHistoryFilters); refreshScheduled(1); }} />}
        {tab==="New Check" && <NewCheck onRefreshHistory={() => refreshHistory(1, historyFilters)} onRefreshScheduled={() => refreshScheduled(1)} prefill={newCheckPrefill} />}
        {tab==="Scheduled" && <ScheduledList scheduled={scheduled} onRefresh={() => refreshScheduled(scheduledPage, scheduledSearch, scheduledFilters)}
          page={scheduledPage} total={scheduledTotal} pageSize={PAGE_SIZE}
          onPage={(p) => setScheduledPage(p)}
          search={scheduledSearch}
          onSearch={(s) => { setScheduledSearch(s); setScheduledPage(1); }}
          filters={scheduledFilters}
          dispatch={(action: ScheduledFiltersAction) => { dispatchScheduledFilters(action); setScheduledPage(1); }}
          loading={scheduledLoading} />}
        {tab==="History" && <HistoryTab history={history} loading={historyLoading}
          filters={historyFilters} dispatch={dispatchHistoryFilters}
          allCount={historyAllCount} correctCount={correctTotal} incorrectCount={incorrectTotal} failedCount={failedTotal}
          page={historyPage} total={historyTotal} pageSize={PAGE_SIZE}
          onPage={(p) => setHistoryPage(p)}
          onRefresh={() => refreshHistory(historyPage, historyFilters)}
          onRerun={handleRerun}
          onApprovalChange={() => refreshHistory(historyPage, historyFilters)}
          pinnedRecord={pinnedRecord} pinnedLoading={pinnedLoading} onClearPinned={clearPinnedRecord}
          onRefreshPinned={refreshPinnedRecord} />}
      </div>
      <style>{`
        html,body{margin:0;padding:0;}
        @keyframes spin{to{transform:rotate(360deg)}}
        input[type=date]::-webkit-calendar-picker-indicator,input[type=time]::-webkit-calendar-picker-indicator{filter:invert(0.4)}
        .PhoneInput { display:flex; align-items:center; gap:6px; }
        .PhoneInputInput { background:transparent; border:none; outline:none; color:#FFFFFF; font-size:14px; flex:1; padding:8px 0; }
        .PhoneInputInput::placeholder { color:#3A6A75; }
        .PhoneInputCountrySelect { background:#00242E; color:#FFFFFF; border:none; outline:none; cursor:pointer; font-size:13px; padding:4px 2px; }
        .PhoneInputCountrySelectArrow { color:#7AACB5; margin-left:4px; }
        .PhoneInputCountryIcon { width:20px; height:14px; }
        /* Filter rail / drawer scrollbar — themed to match the deep-teal palette
           instead of the OS default. Firefox gets the two-colour shorthand; WebKit
           gets a transparent track so the rail background shows through. */
        .sc-scroll { scrollbar-width: thin; scrollbar-color: #005870 transparent; }
        .sc-scroll::-webkit-scrollbar { width:8px; height:8px; }
        .sc-scroll::-webkit-scrollbar-track { background:transparent; }
        .sc-scroll::-webkit-scrollbar-thumb { background:#005870; border-radius:4px; border:2px solid transparent; background-clip:content-box; }
        .sc-scroll::-webkit-scrollbar-thumb:hover { background:#7AACB5; background-clip:content-box; }
        .sc-scroll::-webkit-scrollbar-corner { background:transparent; }
      `}</style>
    </div>
  );
}

export default function App() {
  return (
    <ProtectedRoute>
      <AuthenticatedApp />
    </ProtectedRoute>
  );
}
